import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import mhApi from '../services/marketing-hub';
import MhMake from './MhMake';
import './MarketingHub.css';

// Logo officiel Make (mark simpleicons), couleur de marque #6D00CC.
const MakeLogo = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="#6D00CC" style={{ verticalAlign: '-2px', marginRight: '3px' }} aria-hidden="true">
    <path d="M13.38 3.498c-.27 0-.511.19-.566.465L9.85 18.986a.578.578 0 0 0 .453.678l4.095.826a.58.58 0 0 0 .682-.455l2.963-15.021a.578.578 0 0 0-.453-.678l-4.096-.826a.589.589 0 0 0-.113-.012zm-5.876.098a.576.576 0 0 0-.516.318L.062 17.697a.575.575 0 0 0 .256.774l3.733 1.877a.578.578 0 0 0 .775-.258l6.926-13.781a.577.577 0 0 0-.256-.776L7.762 3.658a.571.571 0 0 0-.258-.062zm11.74.115a.576.576 0 0 0-.576.576v15.426c0 .318.258.578.576.578h4.178a.58.58 0 0 0 .578-.578V4.287a.578.578 0 0 0-.578-.576Z"/>
  </svg>
);

// Marque Gandi : « g. » dans la couleur de marque (#3A9CA6). Glyphe simple,
// remplaçable par le SVG officiel si besoin.
const GandiLogo = () => (
  <span aria-hidden="true" style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 16, height: 16, borderRadius: 4, background: '#1c1c1c', color: '#fff',
    fontWeight: 800, fontSize: 11, marginRight: 4, verticalAlign: '-3px', lineHeight: 1,
  }}>g.</span>
);

/* ════════════════════════════ Helpers ════════════════════════════ */

const SITE_TYPES = [
  { value: 'annonces',   label: '📑 Annonces (emploi/immo)' },
  { value: 'concession', label: '🚗 Concession auto' },
  { value: 'ecommerce',  label: '🛒 E-commerce' },
  { value: 'vitrine',    label: '🏛️ Vitrine / corporate' },
];
const KANBAN_COLS = [
  { key: 'backlog',     label: 'Backlog' },
  { key: 'todo',        label: 'À faire' },
  { key: 'in_progress', label: 'En cours' },
  { key: 'review',      label: 'Revue' },
  { key: 'done',        label: 'Fait' },
];
// Colonnes kanban des tâches d'un projet (plus simple que celui des projets).
const TASK_KANBAN_COLS = [
  { key: 'todo',        label: 'À faire' },
  { key: 'in_progress', label: 'En cours' },
  { key: 'review',      label: 'Revue' },
  { key: 'done',        label: 'Fait' },
];
const PRIORITY_LABELS = { low: 'Basse', normal: 'Normale', high: 'Haute', urgent: 'Urgente' };
const PRIORITY_ORDER  = { low: 0, normal: 1, high: 2, urgent: 3 };
const STATUS_LABELS = Object.fromEntries(KANBAN_COLS.map(c => [c.key, c.label]));
const STATUS_ORDER  = Object.fromEntries(KANBAN_COLS.map((c, i) => [c.key, i]));
const DIMENSION_LABELS = {
  seo: 'SEO', seo_local: 'SEO local', sea: 'SEA', geo: 'GEO', perf: 'Performance',
  security: 'Sécurité', rgpd: 'RGPD', navigability: 'Navigabilité', accessibility: 'Accessibilité',
  conversion: 'Conversion', listings: 'Stock/Annonces', domain_ssl: 'Domaine/SSL', uptime: 'Uptime',
};
const SEVERITY = {
  critical: { label: 'Critique', cls: 'status-error' },
  high:     { label: 'Élevé',    cls: 'status-error' },
  medium:   { label: 'Moyen',    cls: 'status-warning' },
  low:      { label: 'Faible',   cls: 'status-info' },
  info:     { label: 'Info',     cls: 'status-info' },
};

// Bande de couleur d'un score /10 → token sémantique (pas de hex hardcodé).
function scoreBand(score) {
  if (score == null) return { bg: 'var(--bg-muted)', fg: 'var(--text-muted)' };
  if (score >= 7.5) return { bg: 'var(--color-success-light)', fg: 'var(--color-success)' };
  if (score >= 5)   return { bg: 'var(--color-warning-light)', fg: 'var(--color-warning)' };
  return { bg: 'var(--color-error-light)', fg: 'var(--color-error)' };
}
function ScoreChip({ score, label }) {
  const b = scoreBand(score);
  return (
    <span className="mh-chip" style={{ background: b.bg, color: b.fg }} title={label || ''}>
      {score == null ? '—' : Number(score).toFixed(1)}
    </span>
  );
}
function fmtDuration(sec) {
  if (!sec || sec < 0) return '0h00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h${String(m).padStart(2, '0')}`;
}
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }

/* ════════════════════════════ Timer bar (persistant) ════════════════════════════ */

function TimerBar({ projects, onChange }) {
  const [timer, setTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [projectId, setProjectId] = useState('');
  const tick = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await mhApi.getTimer();
      setTimer(data);
      if (data) setProjectId(String(data.project_id || ''));
    } catch { /* silencieux */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!timer) { setElapsed(0); return; }
    const base = Math.floor((Date.now() - new Date(timer.started_at).getTime()) / 1000);
    setElapsed(base);
    tick.current = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(tick.current);
  }, [timer]);

  const start = async () => {
    try { await mhApi.startTimer({ project_id: projectId || null }); await load(); onChange && onChange(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur démarrage'); }
  };
  const stop = async () => {
    try { await mhApi.stopTimer({ project_id: projectId || null }); setTimer(null); await load(); onChange && onChange(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur arrêt'); }
  };

  return (
    <div className={`mh-timerbar ${timer ? 'running' : ''}`}>
      <span className="mh-timerbar-icon">⏱️</span>
      {timer ? (
        <>
          <span className="mh-timer-elapsed">{fmtDuration(elapsed)}</span>
          <span className="mh-timer-project">{timer.project_title || 'Sans projet'}</span>
          <button className="page-action-btn mh-timer-stop" onClick={stop}>■ Arrêter</button>
        </>
      ) : (
        <>
          <select className="page-select" value={projectId} onChange={e => setProjectId(e.target.value)}>
            <option value="">Sans projet</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <button className="page-action-btn" onClick={start}>▶ Pointer</button>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════ Composant principal ════════════════════════════ */

export default function MarketingHub({ user, setHeaderAction, editMode }) {
  const [tab, setTab] = useState('cockpit');
  const [clients, setClients] = useState([]);
  const [tools, setTools] = useState([]);
  const [services, setServices] = useState([]);
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = user?.role === 'super-admin';

  const reloadClients = useCallback(async () => {
    try { const { data } = await mhApi.listClients(); setClients(data); } catch { /* */ }
  }, []);
  const reloadTools = useCallback(async () => {
    try { const { data } = await mhApi.listTools(); setTools(data); } catch { /* */ }
  }, []);
  const reloadServices = useCallback(async () => {
    try { const { data } = await mhApi.listServices(); setServices(data); } catch { /* */ }
  }, []);
  const reloadProjects = useCallback(async () => {
    try { const { data } = await mhApi.listProjects(); setProjects(data); } catch { /* */ }
  }, []);
  const reloadMembers = useCallback(async () => {
    try { const { data } = await mhApi.listMembers(); setMembers(data); } catch { /* */ }
  }, []);
  const reloadSites = useCallback(async () => {
    try { const { data } = await mhApi.listSites(); setSites(data); } catch { /* */ }
  }, []);
  const reloadAudits = useCallback(async () => {
    try { const { data } = await mhApi.listAudits(); setAudits(data); } catch { /* */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([reloadClients(), reloadTools(), reloadServices(), reloadSites(), reloadProjects(), reloadMembers(), reloadAudits()]);
      setLoading(false);
    })();
  }, [reloadClients, reloadTools, reloadServices, reloadSites, reloadProjects, reloadMembers, reloadAudits]);

  // Bouton d'action du header selon l'onglet
  const [modal, setModal] = useState(null); // { type, data }
  const [actionsReloadKey, setActionsReloadKey] = useState(0);
  useEffect(() => {
    if (!setHeaderAction) return;
    if (tab === 'clients') setHeaderAction({ primary: { label: '+ Client', onClick: () => setModal({ type: 'client' }) } });
    else if (tab === 'outils') setHeaderAction({ primary: { label: '+ Outil', onClick: () => setModal({ type: 'tool' }) } });
    else if (tab === 'services') setHeaderAction({ primary: { label: '+ Service', onClick: () => setModal({ type: 'service' }) } });
    else if (tab === 'sites') setHeaderAction({ primary: { label: '+ Site', onClick: () => setModal({ type: 'site' }) } });
    else if (tab === 'projets') setHeaderAction({ primary: { label: '+ Projet', onClick: () => setModal({ type: 'project' }) } });
    else if (tab === 'actions') setHeaderAction({ primary: { label: '+ Action', onClick: () => setModal({ type: 'action', onSaved: () => setActionsReloadKey(k => k + 1) }) } });
    else if (tab !== 'gandi') setHeaderAction(null); // l'onglet Gandi gère lui-même son action header
    return () => setHeaderAction && setHeaderAction(null);
  }, [tab, setHeaderAction]);

  return (
    <div className="page-container mh">
      <div className="page-tabs">
        <button className={tab === 'cockpit' ? 'active' : ''} onClick={() => setTab('cockpit')}>📊 Cockpit</button>
        <button className={tab === 'clients' ? 'active' : ''} onClick={() => setTab('clients')}>👥 Clients</button>
        <button className={tab === 'sites' ? 'active' : ''} onClick={() => setTab('sites')}>🛰️ Sites</button>
        <button className={tab === 'projets' ? 'active' : ''} onClick={() => setTab('projets')}>🗂️ Projets</button>
        <button className={tab === 'actions' ? 'active' : ''} onClick={() => setTab('actions')}>🗓️ Actions</button>
        <button className={tab === 'pointage' ? 'active' : ''} onClick={() => setTab('pointage')}>⏱️ Pointage</button>
        <button className={tab === 'audits' ? 'active' : ''} onClick={() => setTab('audits')}>🔎 Audits</button>
        <button className={tab === 'make' ? 'active' : ''} onClick={() => setTab('make')}><MakeLogo /> Make</button>
        <button className={tab === 'outils' ? 'active' : ''} onClick={() => setTab('outils')}>🧰 Outils</button>
        <button className={tab === 'services' ? 'active' : ''} onClick={() => setTab('services')}>📚 Catalogue</button>
        <button className={tab === 'gandi' ? 'active' : ''} onClick={() => setTab('gandi')}><GandiLogo /> Gandi</button>
      </div>

      <TimerBar projects={projects} onChange={reloadProjects} />

      {loading ? <div className="mh-loading">Chargement…</div> : (
        <>
          {tab === 'cockpit'  && <Cockpit sites={sites} projects={projects} audits={audits} onGoto={setTab} />}
          {tab === 'clients'  && <ClientsView clients={clients} reload={reloadClients} isSuperAdmin={isSuperAdmin} editMode={editMode} openModal={setModal} />}
          {tab === 'outils'   && <ToolsView tools={tools} reload={reloadTools} isSuperAdmin={isSuperAdmin} editMode={editMode} openModal={setModal} />}
          {tab === 'services' && <ServicesView services={services} reload={reloadServices} isSuperAdmin={isSuperAdmin} editMode={editMode} openModal={setModal} />}
          {tab === 'sites'    && <SitesView sites={sites} reload={reloadSites} isSuperAdmin={isSuperAdmin} editMode={editMode} openModal={setModal} />}
          {tab === 'projets'  && <ProjectsView projects={projects} sites={sites} clients={clients} members={members} reload={reloadProjects} isSuperAdmin={isSuperAdmin} openModal={setModal} />}
          {tab === 'actions'  && <ActionsView sites={sites} isSuperAdmin={isSuperAdmin} editMode={editMode} openModal={setModal} reloadKey={actionsReloadKey} />}
          {tab === 'pointage' && <PointageView projects={projects} user={user} isSuperAdmin={isSuperAdmin} />}
          {tab === 'audits'   && <AuditsView audits={audits} reload={reloadAudits} reloadProjects={reloadProjects} isSuperAdmin={isSuperAdmin} />}
          {tab === 'make'     && <MhMake user={user} />}
          {tab === 'gandi'    && <GandiView clients={clients} setHeaderAction={setHeaderAction} />}
        </>
      )}

      {modal?.type === 'client' && <ClientModal client={modal.data} onClose={() => setModal(null)} onSaved={() => { setModal(null); reloadClients(); }} />}
      {modal?.type === 'tool' && <ToolModal tool={modal.data} onClose={() => setModal(null)} onSaved={() => { setModal(null); reloadTools(); }} />}
      {modal?.type === 'service' && <ServiceModal service={modal.data} poles={[...new Set(services.map(s => s.pole))]} onClose={() => setModal(null)} onSaved={() => { setModal(null); reloadServices(); }} />}
      {modal?.type === 'site' && <SiteModal site={modal.data} clients={clients} onClose={() => setModal(null)} onSaved={() => { setModal(null); reloadSites(); reloadAudits(); }} />}
      {modal?.type === 'project' && <ProjectModal project={modal.data} sites={sites} clients={clients} members={members} user={user} onClose={() => setModal(null)} onSaved={() => { setModal(null); reloadProjects(); }} />}
      {modal?.type === 'action' && <ActionModal action={modal.data} presetSiteId={modal.siteId} sites={sites} onClose={() => setModal(null)} onSaved={() => { setModal(null); modal.onSaved && modal.onSaved(); }} />}
    </div>
  );
}

/* ════════════════════════════ Cockpit ════════════════════════════ */

function Cockpit({ sites, projects, audits, onGoto }) {
  const auditBySite = useMemo(() => Object.fromEntries(audits.map(a => [a.site_id, a])), [audits]);
  const today = new Date(); today.setHours(0,0,0,0);

  const dims = ['seo', 'seo_local', 'conversion', 'perf', 'geo', 'security'];
  const activeSites = sites.filter(s => s.active);
  const activeProjects = projects.filter(p => p.status !== 'done').slice(0, 8);
  const inProgressCount = projects.filter(p => p.status === 'in_progress').length;
  const overdueCount = projects.filter(p => p.status !== 'done' && p.due_date && new Date(p.due_date) < today).length;
  const totalSeconds = projects.reduce((a, p) => a + Number(p.time_seconds || 0), 0);
  const auditedSites = audits.filter(a => a.global_score != null);
  const avgScore = auditedSites.length
    ? (auditedSites.reduce((s, a) => s + Number(a.global_score), 0) / auditedSites.length).toFixed(1)
    : null;
  const alertCount = auditedSites.filter(a => Number(a.global_score) < 5).length;
  const scoreAccent = avgScore == null ? 'var(--color-accent)' : Number(avgScore) >= 7.5 ? 'var(--color-success)' : Number(avgScore) >= 5 ? 'var(--color-warning)' : 'var(--color-error)';

  return (
    <div className="mh-cockpit">
      {/* KPI strip */}
      <div className="mh-kpi-strip">
        <div className="mh-kpi-card" style={{ '--_accent': 'var(--color-accent)' }}>
          <span className="mh-kpi-label">Sites actifs</span>
          <span className="mh-kpi-value">{activeSites.length}</span>
          <span className="mh-kpi-sub">{alertCount > 0 ? `${alertCount} avec score < 5` : 'aucune alerte score'}</span>
        </div>
        <div className="mh-kpi-card" style={{ '--_accent': 'var(--color-warning)' }}>
          <span className="mh-kpi-label">Projets actifs</span>
          <span className="mh-kpi-value">{activeProjects.length}</span>
          <span className="mh-kpi-sub">
            {inProgressCount} en cours
            {overdueCount > 0 && <span style={{ color: 'var(--color-error)', marginLeft: 6 }}>· {overdueCount} en retard</span>}
          </span>
        </div>
        <div className="mh-kpi-card" style={{ '--_accent': 'var(--color-decision)' }}>
          <span className="mh-kpi-label">Temps cumulé</span>
          <span className="mh-kpi-value">{fmtDuration(totalSeconds)}</span>
          <span className="mh-kpi-sub">tous projets confondus</span>
        </div>
        <div className="mh-kpi-card" style={{ '--_accent': scoreAccent }}>
          <span className="mh-kpi-label">Score moyen</span>
          <span className="mh-kpi-value" style={{ color: scoreAccent }}>{avgScore ?? '—'}</span>
          <span className="mh-kpi-sub">{auditedSites.length} site(s) audité(s)</span>
        </div>
      </div>

      {/* Santé des sites */}
      <section className="mh-panel">
        <h3>Santé des sites <button className="mh-link" onClick={() => onGoto('audits')}>tout voir →</button></h3>
        <div className="table-container">
          <table className="mh-matrix">
            <thead>
              <tr><th>Site</th>{dims.map(d => <th key={d}>{DIMENSION_LABELS[d]}</th>)}<th>Global</th></tr>
            </thead>
            <tbody>
              {activeSites.map(s => {
                const a = auditBySite[s.id];
                const scoreMap = Object.fromEntries((a?.scores || []).map(x => [x.dimension, x.score]));
                return (
                  <tr key={s.id}>
                    <td className="mh-site-cell">{s.name}</td>
                    {dims.map(d => <td key={d}><ScoreChip score={scoreMap[d]} label={DIMENSION_LABELS[d]} /></td>)}
                    <td><ScoreChip score={a?.global_score} label="Score global" /></td>
                  </tr>
                );
              })}
              {activeSites.length === 0 && <tr><td colSpan={dims.length + 2} className="mh-empty">Aucun site. Ajoutez-en dans l'onglet Sites.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {/* Projets en cours */}
      <section className="mh-panel">
        <h3>Projets en cours <button className="mh-link" onClick={() => onGoto('projets')}>kanban →</button></h3>
        <div className="table-container">
          <table>
            <thead>
              <tr><th>Projet</th><th>Responsable</th><th>Statut</th><th>Temps</th><th>Échéance</th></tr>
            </thead>
            <tbody>
              {activeProjects.map(p => (
                <tr key={p.id}>
                  <td><span className={`mh-team-dot ${p.team}`} /><strong>{p.title}</strong></td>
                  <td className="mh-muted">{p.owner_nom || '—'}</td>
                  <td><span className={`mh-status-pill ${p.status}`}>{KANBAN_COLS.find(c => c.key === p.status)?.label || p.status}</span></td>
                  <td className="mh-muted">{fmtDuration(p.time_seconds)}</td>
                  <td className={p.due_date && new Date(p.due_date) < today && p.status !== 'done' ? 'mh-overdue' : 'mh-muted'}>{fmtDate(p.due_date)}</td>
                </tr>
              ))}
              {activeProjects.length === 0 && <tr><td colSpan={5} className="mh-empty">Aucun projet en cours.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ════════════════════════════ Clients ════════════════════════════ */

const MONTH_FMT = new Intl.DateTimeFormat('fr-FR', { month: 'short', year: '2-digit' });
function fmtMonth(d) { return d ? MONTH_FMT.format(new Date(d)) : '—'; }
const statusLabel = (st) => KANBAN_COLS.find(c => c.key === st)?.label || st;

function ClientsView({ clients, reload, isSuperAdmin, editMode, openModal }) {
  const [search, setSearch] = useState('');
  const [profile, setProfile] = useState(null);
  const [sel, setSel] = useState(new Set());

  const filtered = clients.filter(c => !search ||
    `${c.name} ${c.contact_name || ''} ${c.contact_email || ''}`.toLowerCase().includes(search.toLowerCase()));
  const canDelete = editMode && isSuperAdmin;

  const toggleSel = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = filtered.length > 0 && filtered.every(c => sel.has(c.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(filtered.map(c => c.id)));

  const del = async (c) => {
    if (!window.confirm(`Supprimer le client « ${c.name} » ? Ses projets et sites seront détachés (non supprimés).`)) return;
    try { await mhApi.deleteClient(c.id); reload(); } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  const delSelection = async () => {
    if (!sel.size || !window.confirm(`Supprimer ${sel.size} client(s) ? Leurs projets et sites seront détachés.`)) return;
    try { for (const id of sel) await mhApi.deleteClient(id); setSel(new Set()); reload(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  return (
    <>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
        <span className="mh-count">{filtered.length} client(s)</span>
        {canDelete && sel.size > 0 && (
          <button className="btn-secondary mh-danger" onClick={delSelection}>🗑️ Supprimer la sélection ({sel.size})</button>
        )}
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {canDelete && <th style={{ width: 32 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>}
              <th>Client</th><th>Contact</th><th>Projets</th><th>Sites</th>
              <th>Temps ce mois</th><th>Temps total</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="mh-row" onClick={() => setProfile(c)}>
                {canDelete && (
                  <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggleSel(c.id)} />
                  </td>
                )}
                <td className="mh-site-cell">
                  {c.name}{!c.active && <span className="status-info mh-tag">inactif</span>}
                </td>
                <td>{c.contact_name || '—'}{c.contact_email ? <div className="mh-url">{c.contact_email}</div> : null}</td>
                <td>{Number(c.project_count) || 0}</td>
                <td>{Number(c.site_count) || 0}</td>
                <td><strong>{fmtDuration(Number(c.month_seconds))}</strong></td>
                <td className="mh-muted">{fmtDuration(Number(c.total_seconds))}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Modifier" onClick={() => openModal({ type: 'client', data: c })}>✏️</button>
                    {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => del(c)}>🗑️</button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={canDelete ? 8 : 7} className="mh-empty">Aucun client. Ajoutez-en avec « + Client ».</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {profile && <ClientProfile client={profile} onClose={() => setProfile(null)} />}
    </>
  );
}

function ClientProfile({ client, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { mhApi.getClient(client.id).then(r => setData(r.data)).catch(() => {}); }, [client.id]);
  const monthly = (data?.monthly || []).slice().reverse(); // ascendant pour le graphe

  return (
    <div className="mh-drawer-overlay" onClick={onClose}>
      <div className="mh-drawer" onClick={e => e.stopPropagation()}>
        <div className="mh-drawer-head">
          <h2>{client.name}{!client.active && <span className="status-info mh-tag">inactif</span>}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mh-drawer-body">
          <div className="mh-kv">
            <div><span>Contact</span>{client.contact_name || '—'}</div>
            <div><span>Email</span>{client.contact_email ? <a className="mh-link" href={`mailto:${client.contact_email}`}>{client.contact_email}</a> : '—'}</div>
            <div><span>Téléphone</span>{client.contact_phone ? <a className="mh-link" href={`tel:${client.contact_phone}`}>{client.contact_phone}</a> : '—'}</div>
            <div><span>Temps ce mois</span>{fmtDuration(Number(client.month_seconds))}</div>
            <div><span>Temps total</span>{fmtDuration(Number(client.total_seconds))}</div>
          </div>
          {client.notes && <p className="mh-muted">{client.notes}</p>}

          <h3>Temps par mois</h3>
          {monthly.length ? (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={monthly.map(m => ({ m: fmtMonth(m.month), h: Number(m.total_hours) }))}>
                  <XAxis dataKey="m" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} width={28} />
                  <Tooltip formatter={(v) => [`${v} h`, 'Temps']} />
                  <Line type="monotone" dataKey="h" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <table className="mh-mini-table">
                <tbody>
                  {(data?.monthly || []).map(m => (
                    <tr key={m.month}><td>{fmtMonth(m.month)}</td><td className="mh-right"><strong>{fmtDuration(Number(m.total_seconds))}</strong></td></tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : <p className="mh-muted">Aucun temps pointé sur les projets de ce client.</p>}

          <h3>Projets ({(data?.projects || []).length})</h3>
          {(data?.projects || []).length ? (
            <table className="mh-mini-table">
              <tbody>
                {data.projects.map(p => (
                  <tr key={p.id}>
                    <td><span className={`mh-team-dot ${p.team}`} />{p.title}{p.archived && <span className="mh-tag mh-muted">archivé</span>}</td>
                    <td className="mh-muted">{statusLabel(p.status)}</td>
                    <td className="mh-right">{fmtDuration(Number(p.time_seconds))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="mh-muted">Aucun projet rattaché.</p>}

          <h3>Sites ({(data?.sites || []).length})</h3>
          {(data?.sites || []).length ? (
            <table className="mh-mini-table">
              <tbody>
                {data.sites.map(s => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td className="mh-muted">{SITE_TYPES.find(t => t.value === s.site_type)?.label || s.site_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="mh-muted">Aucun site rattaché.</p>}
        </div>
      </div>
    </div>
  );
}

function ClientModal({ client, onClose, onSaved }) {
  const [f, setF] = useState({ name: '', contact_name: '', contact_email: '', contact_phone: '', notes: '', active: true, ...client });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name || !f.name.trim()) { alert('Nom requis'); return; }
    const payload = {
      name: f.name.trim(),
      contact_name: f.contact_name || null,
      contact_email: f.contact_email || null,
      contact_phone: f.contact_phone || null,
      notes: f.notes || null,
      active: !!f.active,
    };
    try {
      if (client?.id) await mhApi.updateClient(client.id, payload);
      else await mhApi.createClient(payload);
      onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{client?.id ? 'Modifier' : 'Nouveau'} client</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="mh-form-grid">
            <label className="mh-col2">Nom du client<input value={f.name} onChange={e => set('name', e.target.value)} /></label>
            <label>Contact (nom)<input value={f.contact_name || ''} onChange={e => set('contact_name', e.target.value)} /></label>
            <label>Email<input type="email" value={f.contact_email || ''} onChange={e => set('contact_email', e.target.value)} placeholder="contact@exemple.fr" /></label>
            <label>Téléphone<input value={f.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} /></label>
            <label className="mh-check"><input type="checkbox" checked={!!f.active} onChange={e => set('active', e.target.checked)} /> Client actif</label>
            <label className="mh-col2">Notes<textarea value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════ Outils & Accès ════════════════════════════ */

const TOOL_CATEGORIES = ['Analytics', 'Automatisation', 'Autre', 'CMS / Site', 'CRM', 'Design', 'Emailing', 'Gestion de projet', 'Hébergement', 'IA', 'Nom de domaine', 'Paiement', 'Publicité', 'Réseaux sociaux', 'Stockage'];
const COST_PERIODS = [{ value: 'monthly', label: '/ mois' }, { value: 'annual', label: '/ an' }, { value: 'one_time', label: 'one-shot' }, { value: 'free', label: 'gratuit' }];
const TOOL_STATUS = { active: { label: 'Actif', cls: 'status-success' }, trial: { label: 'Essai', cls: 'status-info' }, to_cancel: { label: 'À résilier', cls: 'status-warning' }, cancelled: { label: 'Résilié', cls: 'status-error' } };
const CRITICALITY = { critical: { label: 'Critique', cls: 'status-error' }, important: { label: 'Important', cls: 'status-warning' }, secondary: { label: 'Secondaire', cls: 'status-info' } };

const toolMonthly = (t) => {
  if (t.status === 'cancelled' || !t.cost_amount) return 0;
  const a = Number(t.cost_amount);
  if (t.cost_period === 'monthly') return a;
  if (t.cost_period === 'annual') return a / 12;
  return 0;
};
const eur0 = (n) => `${Math.round(n).toLocaleString('fr-FR')} €`;
const daysUntil = (d) => (d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null);

function ToolsView({ tools, reload, isSuperAdmin, editMode, openModal }) {
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('');
  const [profile, setProfile] = useState(null);
  const [sel, setSel] = useState(new Set());
  const canDelete = editMode && isSuperAdmin;

  const filtered = tools.filter(t =>
    (!cat || t.category === cat) &&
    (!search || `${t.name} ${t.category || ''} ${t.admin_email || ''} ${t.owner_name || ''}`.toLowerCase().includes(search.toLowerCase())));

  // Synthèse
  const monthlyTotal = tools.reduce((a, t) => a + toolMonthly(t), 0);
  const activeCount = tools.filter(t => t.status !== 'cancelled').length;
  const renewals = tools.filter(t => { const d = daysUntil(t.renewal_date); return d != null && d >= 0 && d <= 60 && t.status !== 'cancelled'; });
  const no2fa = tools.filter(t => !t.twofa && (t.status === 'active' || t.status === 'trial'));

  const toggleSel = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = filtered.length > 0 && filtered.every(t => sel.has(t.id));
  const del = async (t) => { if (!window.confirm(`Supprimer l'outil « ${t.name} » ?`)) return; try { await mhApi.deleteTool(t.id); reload(); } catch (e) { alert(e.response?.data?.error || 'Erreur'); } };
  const delSelection = async () => { if (!sel.size || !window.confirm(`Supprimer ${sel.size} outil(s) ?`)) return; try { for (const id of sel) await mhApi.deleteTool(id); setSel(new Set()); reload(); } catch (e) { alert(e.response?.data?.error || 'Erreur'); } };

  return (
    <>
      {/* Synthèse */}
      <div className="mh-tools-kpis">
        <div className="mh-panel"><h3>Coût mensuel</h3><div className="mh-bigfig">{eur0(monthlyTotal)}</div><div className="mh-sub">soit {eur0(monthlyTotal * 12)}/an · {activeCount} outil(s) actif(s)</div></div>
        <div className="mh-panel"><h3>⏰ Renouvellements (≤ 60 j)</h3><div className="mh-bigfig">{renewals.length}</div><div className="mh-sub">{renewals.slice(0, 3).map(t => t.name).join(', ') || 'aucun à venir'}</div></div>
        <div className="mh-panel"><h3>🔓 Sans 2FA</h3><div className="mh-bigfig" style={{ color: no2fa.length ? 'var(--color-error)' : 'var(--color-success)' }}>{no2fa.length}</div><div className="mh-sub">outils actifs sans double authentification</div></div>
      </div>

      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className={`page-select ${cat ? 'active-filter' : ''}`} value={cat} onChange={e => setCat(e.target.value)}>
          <option value="">Toutes catégories</option>
          {TOOL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="mh-count">{filtered.length} outil(s)</span>
        {canDelete && sel.size > 0 && <button className="btn-secondary mh-danger" onClick={delSelection}>🗑️ Supprimer la sélection ({sel.size})</button>}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              {canDelete && <th style={{ width: 32 }}><input type="checkbox" checked={allSelected} onChange={() => setSel(allSelected ? new Set() : new Set(filtered.map(t => t.id)))} /></th>}
              <th>Outil</th><th>Admin (email)</th><th>Coût</th><th>Renouvellement</th><th>2FA</th><th>Statut</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => {
              const d = daysUntil(t.renewal_date);
              const soon = d != null && d >= 0 && d <= 30 && t.status !== 'cancelled';
              return (
                <tr key={t.id} className="mh-row" onClick={() => setProfile(t)}>
                  {canDelete && <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={sel.has(t.id)} onChange={() => toggleSel(t.id)} /></td>}
                  <td className="mh-site-cell">
                    {t.name}
                    {t.criticality === 'critical' && <span className="status-error mh-tag">critique</span>}
                    <div className="mh-url">{t.category || '—'}{t.plan ? ` · ${t.plan}` : ''}</div>
                  </td>
                  <td>{t.admin_email || '—'}{t.admin_name ? <div className="mh-url">{t.admin_name}</div> : null}</td>
                  <td>{t.cost_amount != null ? `${Number(t.cost_amount).toLocaleString('fr-FR')} ${t.cost_currency || 'EUR'} ${(COST_PERIODS.find(p => p.value === t.cost_period) || {}).label || ''}` : '—'}{t.company ? <div className="mh-url">{t.company}</div> : null}</td>
                  <td className={soon ? 'mh-overdue' : 'mh-muted'}>{t.renewal_date ? fmtDate(t.renewal_date) : '—'}{soon ? ` (${d} j)` : ''}</td>
                  <td>{t.twofa ? '✅' : <span className="status-warning mh-tag">non</span>}</td>
                  <td><span className={`${(TOOL_STATUS[t.status] || {}).cls || ''} mh-tag`}>{(TOOL_STATUS[t.status] || {}).label || t.status}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Modifier" onClick={() => openModal({ type: 'tool', data: t })}>✏️</button>
                      {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => del(t)}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={canDelete ? 8 : 7} className="mh-empty">Aucun outil. Ajoutez-en avec « + Outil ».</td></tr>}
          </tbody>
        </table>
      </div>
      {profile && <ToolProfile tool={profile} onClose={() => setProfile(null)} />}
    </>
  );
}

function ToolProfile({ tool, onClose }) {
  const t = tool;
  const period = (COST_PERIODS.find(p => p.value === t.cost_period) || {}).label || '';
  return (
    <div className="mh-drawer-overlay" onClick={onClose}>
      <div className="mh-drawer" onClick={e => e.stopPropagation()}>
        <div className="mh-drawer-head">
          <h2>{t.name}{t.criticality === 'critical' && <span className="status-error mh-tag">critique</span>}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mh-drawer-body">
          {t.url && <a href={t.url} target="_blank" rel="noreferrer" className="mh-link">{t.url} ↗</a>}
          <div className="mh-kv">
            <div><span>Catégorie</span>{t.category || '—'}</div>
            <div><span>Formule</span>{t.plan || '—'}</div>
            <div><span>Statut</span>{(TOOL_STATUS[t.status] || {}).label || t.status}</div>
            <div><span>Criticité</span>{(CRITICALITY[t.criticality] || {}).label || t.criticality}</div>
          </div>

          <h3>Accès & administration</h3>
          <div className="mh-kv">
            <div><span>Admin du compte</span>{t.admin_name || '—'}{t.admin_email ? <> · <a className="mh-link" href={`mailto:${t.admin_email}`}>{t.admin_email}</a></> : ''}</div>
            <div><span>Responsable interne</span>{t.owner_name || '—'}</div>
            <div><span>Login de connexion</span>{t.login_email || '—'}{t.sso ? ' · SSO' : ''}</div>
            <div><span>Double authentification (2FA)</span>{t.twofa ? '✅ activée' : '⚠️ non activée'}</div>
            <div><span>Gestionnaire de mots de passe</span>{t.password_manager || '—'}</div>
            <div><span>Qui a accès</span>{(t.access_users || []).join(', ') || '—'}</div>
          </div>

          <h3>Coûts & renouvellement</h3>
          <div className="mh-kv">
            <div><span>Coût</span>{t.cost_amount != null ? `${Number(t.cost_amount).toLocaleString('fr-FR')} ${t.cost_currency || 'EUR'} ${period}` : '—'}</div>
            <div><span>Équivalent mensuel</span>{eur0(toolMonthly(t))}</div>
            <div><span>Société (qui paie)</span>{t.company || '—'}</div>
            <div><span>Renouvellement</span>{t.renewal_date ? fmtDate(t.renewal_date) : '—'}</div>
            <div><span>Paiement</span>{t.payment_method || '—'}</div>
          </div>

          {t.notes && <><h3>Notes</h3><p className="mh-muted">{t.notes}</p></>}
        </div>
      </div>
    </div>
  );
}

function ToolModal({ tool, onClose, onSaved }) {
  const [f, setF] = useState({
    name: '', category: '', url: '', plan: '',
    admin_name: '', admin_email: '', owner_name: '', login_email: '', sso: false, twofa: false, password_manager: '',
    cost_amount: '', cost_period: 'monthly', cost_currency: 'EUR', company: '', payment_method: '', renewal_date: '',
    status: 'active', criticality: 'important', notes: '', ...tool,
    access_users: (tool?.access_users || []).join(', '),
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name || !f.name.trim()) { alert('Nom requis'); return; }
    const payload = {
      ...f,
      cost_amount: f.cost_amount === '' ? null : Number(f.cost_amount),
      renewal_date: f.renewal_date || null,
      access_users: String(f.access_users || '').split(',').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (tool?.id) await mhApi.updateTool(tool.id, payload);
      else await mhApi.createTool(payload);
      onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{tool?.id ? 'Modifier' : 'Nouvel'} outil</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="mh-form-grid">
            <h4 className="mh-form-section">Identité</h4>
            <label>Nom<input value={f.name} onChange={e => set('name', e.target.value)} placeholder="Brevo, Make, Notion, Gandi…" /></label>
            <label>Catégorie<select value={f.category || ''} onChange={e => set('category', e.target.value)}><option value="">—</option>{TOOL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className="mh-col2">URL de connexion<input value={f.url || ''} onChange={e => set('url', e.target.value)} placeholder="https://app..." /></label>
            <label>Formule / plan<input value={f.plan || ''} onChange={e => set('plan', e.target.value)} placeholder="ex. Business, Core 10k ops" /></label>
            <label>Criticité<select value={f.criticality} onChange={e => set('criticality', e.target.value)}>{Object.entries(CRITICALITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>
            <label>Statut<select value={f.status} onChange={e => set('status', e.target.value)}>{Object.entries(TOOL_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>

            <h4 className="mh-form-section">Accès & administration</h4>
            <label>Admin du compte (nom)<input value={f.admin_name || ''} onChange={e => set('admin_name', e.target.value)} /></label>
            <label>Admin — email<input type="email" value={f.admin_email || ''} onChange={e => set('admin_email', e.target.value)} placeholder="admin@..." /></label>
            <label>Responsable interne<input value={f.owner_name || ''} onChange={e => set('owner_name', e.target.value)} placeholder="qui gère cet outil" /></label>
            <label>Login de connexion<input value={f.login_email || ''} onChange={e => set('login_email', e.target.value)} placeholder="souvent un compte partagé" /></label>
            <label>Gestionnaire de mots de passe<input value={f.password_manager || ''} onChange={e => set('password_manager', e.target.value)} placeholder="1Password, Bitwarden…" /></label>
            <label className="mh-col2">Qui a accès (séparés par ,)<input value={f.access_users} onChange={e => set('access_users', e.target.value)} placeholder="Marie, Paul, prestataire X…" /></label>
            <label className="mh-check"><input type="checkbox" checked={!!f.sso} onChange={e => set('sso', e.target.checked)} /> Connexion via SSO</label>
            <label className="mh-check"><input type="checkbox" checked={!!f.twofa} onChange={e => set('twofa', e.target.checked)} /> Double authentification (2FA) activée</label>

            <h4 className="mh-form-section">Coûts & renouvellement</h4>
            <label>Montant<input type="number" step="0.01" value={f.cost_amount ?? ''} onChange={e => set('cost_amount', e.target.value)} /></label>
            <label>Périodicité<select value={f.cost_period} onChange={e => set('cost_period', e.target.value)}>{COST_PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></label>
            <label>Date de renouvellement / expiration<input type="date" value={f.renewal_date ? String(f.renewal_date).slice(0, 10) : ''} onChange={e => set('renewal_date', e.target.value)} /></label>
            <label>Société qui supporte le coût<input value={f.company || ''} onChange={e => set('company', e.target.value)} placeholder="ex. Auto Discount, Groupe…" /></label>
            <label>Mode de paiement<input value={f.payment_method || ''} onChange={e => set('payment_method', e.target.value)} placeholder="CB société, prélèvement…" /></label>
            <label className="mh-col2">Notes<textarea value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════ Catalogue de services ════════════════════════════ */

const SERVICE_POLES = ['Digital', 'Leads Management', 'Marque et Contenus', 'Marketing', 'Web'];

function ServicesView({ services, reload, isSuperAdmin, editMode, openModal }) {
  const [search, setSearch] = useState('');
  const [pole, setPole] = useState('');
  const [sel, setSel] = useState(new Set());
  const canDelete = editMode && isSuperAdmin;

  const filtered = services.filter(s =>
    (!pole || s.pole === pole) &&
    (!search || `${s.pole} ${s.service} ${s.description || ''}`.toLowerCase().includes(search.toLowerCase())));

  // Groupes par pôle en préservant l'ordre d'apparition (l'API trie déjà par position)
  const groups = [];
  const idx = {};
  filtered.forEach(s => {
    if (idx[s.pole] === undefined) { idx[s.pole] = groups.length; groups.push({ pole: s.pole, items: [] }); }
    groups[idx[s.pole]].items.push(s);
  });

  const polesForFilter = [...new Set(services.map(s => s.pole))];
  const toggleSel = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const del = async (s) => { if (!window.confirm(`Supprimer le service « ${s.service} » ?`)) return; try { await mhApi.deleteService(s.id); reload(); } catch (e) { alert(e.response?.data?.error || 'Erreur'); } };
  const delSelection = async () => { if (!sel.size || !window.confirm(`Supprimer ${sel.size} service(s) ?`)) return; try { for (const id of sel) await mhApi.deleteService(id); setSel(new Set()); reload(); } catch (e) { alert(e.response?.data?.error || 'Erreur'); } };

  return (
    <>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className={`page-select ${pole ? 'active-filter' : ''}`} value={pole} onChange={e => setPole(e.target.value)}>
          <option value="">Tous les pôles</option>
          {polesForFilter.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="mh-count">{filtered.length} service(s) · {groups.length} pôle(s)</span>
        {canDelete && sel.size > 0 && <button className="btn-secondary mh-danger" onClick={delSelection}>🗑️ Supprimer la sélection ({sel.size})</button>}
      </div>

      {groups.map(g => (
        <div className="table-container mh-service-group" key={g.pole}>
          <table>
            <thead>
              <tr><th colSpan={canDelete ? 3 : 2}>{g.pole} <span className="mh-count">({g.items.length})</span></th></tr>
            </thead>
            <tbody>
              {g.items.map(s => (
                <tr key={s.id} className="mh-row">
                  {canDelete && <td style={{ width: 32 }}><input type="checkbox" checked={sel.has(s.id)} onChange={() => toggleSel(s.id)} /></td>}
                  <td className="mh-site-cell">
                    {s.service}
                    {s.active === false && <span className="status-warning mh-tag">inactif</span>}
                    {s.description ? <div className="mh-url">{s.description}</div> : null}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Modifier" onClick={() => openModal({ type: 'service', data: s })}>✏️</button>
                      {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => del(s)}>🗑️</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {groups.length === 0 && <div className="table-container"><table><tbody><tr><td className="mh-empty">Aucun service. Ajoutez-en avec « + Service ».</td></tr></tbody></table></div>}
    </>
  );
}

function ServiceModal({ service, poles = [], onClose, onSaved }) {
  const knownPoles = [...new Set([...SERVICE_POLES, ...poles])];
  const [f, setF] = useState({ pole: '', service: '', description: '', active: true, ...service });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.pole || !f.pole.trim()) { alert('Pôle requis'); return; }
    if (!f.service || !f.service.trim()) { alert('Service requis'); return; }
    const payload = { pole: f.pole.trim(), service: f.service.trim(), description: f.description || null, active: f.active !== false };
    try {
      if (service?.id) await mhApi.updateService(service.id, payload);
      else await mhApi.createService(payload);
      onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{service?.id ? 'Modifier le' : 'Nouveau'} service</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="mh-form-grid">
            <label>Pôle
              <input list="mh-poles" value={f.pole || ''} onChange={e => set('pole', e.target.value)} placeholder="Web, Marketing…" />
              <datalist id="mh-poles">{knownPoles.map(p => <option key={p} value={p} />)}</datalist>
            </label>
            <label>Statut<select value={f.active === false ? '0' : '1'} onChange={e => set('active', e.target.value === '1')}><option value="1">Actif</option><option value="0">Inactif</option></select></label>
            <label className="mh-col2">Service / expertise<input value={f.service || ''} onChange={e => set('service', e.target.value)} placeholder="ex. Optimisation SEO" /></label>
            <label className="mh-col2">Description<textarea value={f.description || ''} onChange={e => set('description', e.target.value)} placeholder="optionnel" /></label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════ Gandi (domaines) ════════════════════════════ */

const fmtMoney = (v, cur) => v == null ? '—'
  : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === 'EUR' || !cur ? '€' : cur}`;

// En-tête de colonne triable : clic = trier, re-clic = inverser le sens.
function SortTh({ label, col, sort, setSort, className }) {
  const active = sort.key === col;
  const arrow = active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const onClick = () => setSort(active ? { key: col, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'asc' });
  return (
    <th className={className} onClick={onClick} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} title="Trier">
      {label}<span style={{ color: 'var(--color-accent)' }}>{arrow}</span>
    </th>
  );
}

function GandiView({ clients = [], setHeaderAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'expires', dir: 'asc' }); // défaut : expiration la plus proche
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    return mhApi.listGandiDomains()
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshCosts = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: r } = await mhApi.refreshGandiCosts();
      await load();
      alert(`Coûts mis à jour : ${r.updated}/${r.total} domaine(s)${r.failed ? ` — ${r.failed} indisponible(s)` : ''}.`);
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors du rafraîchissement des coûts');
    } finally { setRefreshing(false); }
  }, [load]);

  // Bouton « Rafraîchir les coûts » dans le header (cet onglet gère son action).
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({ secondary: [{ label: refreshing ? '⏳ Rafraîchissement…' : '🔄 Rafraîchir les coûts', onClick: refreshCosts }] });
    return () => setHeaderAction(null);
  }, [setHeaderAction, refreshCosts, refreshing]);

  const assignClient = async (fqdn, clientId) => {
    setSaving(fqdn);
    try {
      await mhApi.updateGandiDomain(fqdn, { client_id: clientId || null });
      const name = clients.find(c => String(c.id) === String(clientId))?.name || null;
      setData(prev => ({ ...prev, domains: prev.domains.map(d => d.fqdn === fqdn ? { ...d, client_id: clientId ? Number(clientId) : null, client_name: name } : d) }));
    } catch (e) {
      alert(e.response?.data?.error || 'Affectation refusée (permission ?)');
    } finally { setSaving(null); }
  };

  if (loading) return <div className="mh-loading">Chargement des domaines Gandi…</div>;
  if (error) return <div className="mh-empty">⚠️ {error}</div>;
  if (!data?.configured) return (
    <div className="mh-empty">
      Gandi non configuré. Ajoutez <code>GANDI_API_KEY</code> (Personal Access Token, accès « domaines ») dans le <code>.env</code> du backend, puis redémarrez.
    </div>
  );

  const domains = data.domains || [];
  const expiring = domains.filter(d => { const n = daysUntil(d.expires_at); return n != null && n >= 0 && n <= 60; });
  const atRisk = expiring.filter(d => d.autorenew === false);
  const totalCost = domains.reduce((s, d) => s + (d.cout_renouvellement || 0), 0);

  const cmp = (a, b) => {
    let va, vb;
    switch (sort.key) {
      case 'client': va = (a.client_name || '').toLowerCase(); vb = (b.client_name || '').toLowerCase(); break;
      case 'cout': va = a.cout_renouvellement; vb = b.cout_renouvellement; break;
      case 'autorenew': va = a.autorenew === true ? 1 : a.autorenew === false ? 0 : -1; vb = b.autorenew === true ? 1 : b.autorenew === false ? 0 : -1; break;
      case 'expires': va = daysUntil(a.expires_at); vb = daysUntil(b.expires_at); break;
      default: va = (a.fqdn || '').toLowerCase(); vb = (b.fqdn || '').toLowerCase();
    }
    // null/undefined toujours en fin de liste, quel que soit le sens
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const r = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === 'asc' ? r : -r;
  };

  const filtered = domains
    .filter(d => !search || `${d.fqdn || ''} ${d.client_name || ''}`.toLowerCase().includes(search.toLowerCase()))
    .sort(cmp);

  return (
    <>
      <div className="mh-tools-kpis">
        <div className="mh-panel"><h3>Domaines</h3><div className="mh-bigfig">{domains.length}</div><div className="mh-sub">gérés sur Gandi</div></div>
        <div className="mh-panel"><h3>⏰ Expirent ≤ 60 j</h3><div className="mh-bigfig">{expiring.length}</div><div className="mh-sub">{expiring.slice(0, 3).map(d => d.fqdn).join(', ') || 'aucun'}</div></div>
        <div className="mh-panel"><h3>⚠️ À risque</h3><div className="mh-bigfig" style={{ color: atRisk.length ? 'var(--color-error)' : 'var(--color-success)' }}>{atRisk.length}</div><div className="mh-sub">expirent bientôt SANS renouvellement auto</div></div>
        <div className="mh-panel"><h3>💶 Coût annuel</h3><div className="mh-bigfig">{totalCost ? fmtMoney(totalCost, 'EUR') : '—'}</div><div className="mh-sub">renouvellement TTC (estimé)</div></div>
      </div>

      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher un domaine ou un client..." value={search} onChange={e => setSearch(e.target.value)} />
        <span className="mh-count">{filtered.length} domaine(s)</span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <SortTh label="Domaine" col="fqdn" sort={sort} setSort={setSort} />
              <SortTh label="Client" col="client" sort={sort} setSort={setSort} />
              <th>Statut</th>
              <SortTh label="Expiration" col="expires" sort={sort} setSort={setSort} />
              <SortTh label="Coût / an" col="cout" sort={sort} setSort={setSort} />
              <SortTh label="Renouv. auto" col="autorenew" sort={sort} setSort={setSort} />
            </tr>
          </thead>
          <tbody>
            {filtered.map(d => {
              const n = daysUntil(d.expires_at);
              const soon = n != null && n >= 0 && n <= 30;
              const expired = n != null && n < 0;
              // On masque les statuts de verrouillage normaux (…Prohibited) pour ne montrer que les anomalies.
              const flags = (d.status || []).filter(s => !/Prohibited$/i.test(s) && !/^ok$/i.test(s));
              return (
                <tr key={d.fqdn}>
                  <td className="mh-site-cell">{d.fqdn}</td>
                  <td>
                    <select
                      className="page-select"
                      value={d.client_id || ''}
                      disabled={saving === d.fqdn}
                      onChange={e => assignClient(d.fqdn, e.target.value)}
                    >
                      <option value="">— Non affecté —</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </td>
                  <td>{expired ? <span className="status-error mh-tag">expiré</span>
                    : flags.length ? flags.map((s, i) => <span key={i} className="status-warning mh-tag">{s}</span>)
                    : <span className="status-success mh-tag">actif</span>}</td>
                  <td className={expired || soon ? 'mh-overdue' : 'mh-muted'}>{d.expires_at ? fmtDate(d.expires_at) : '—'}{n != null && n >= 0 ? ` (${n} j)` : ''}</td>
                  <td>{fmtMoney(d.cout_renouvellement, d.cout_devise)}</td>
                  <td>{d.autorenew === true ? '✅' : d.autorenew === false ? <span className="status-warning mh-tag">désactivé</span> : '—'}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="mh-empty">Aucun domaine.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ════════════════════════════ Sites ════════════════════════════ */

function SitesView({ sites, reload, isSuperAdmin, editMode, openModal }) {
  const [search, setSearch] = useState('');
  const [profile, setProfile] = useState(null);
  const filtered = sites.filter(s => !search || `${s.name} ${s.url} ${s.zone || ''}`.toLowerCase().includes(search.toLowerCase()));

  const del = async (s) => {
    if (!window.confirm(`Supprimer le site « ${s.name} » ?`)) return;
    try { await mhApi.deleteSite(s.id); reload(); } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  return (
    <>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
        <span className="mh-count">{filtered.length} site(s)</span>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Site</th><th>Type</th><th>Zone</th><th>Gestionnaire</th>
              <th>Ads</th><th>GSC</th><th>GMB</th><th>Hébergeur</th><th>Coût/mois</th><th>Audit</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id} className="mh-row" onClick={() => setProfile(s)}>
                <td className="mh-site-cell">{s.name}<div className="mh-url">{s.url}</div></td>
                <td>{SITE_TYPES.find(t => t.value === s.site_type)?.label || s.site_type}</td>
                <td>{s.zone || '—'}</td>
                <td>{s.manager_type === 'provider' ? `🏢 ${s.provider_name || 'Prestataire'}` : '🏠 Interne'}</td>
                <td>{s.ads_exists ? '✅' : '—'}</td>
                <td>{s.gsc_exists ? '✅' : '—'}</td>
                <td>{s.gmb_exists ? '✅' : '—'}</td>
                <td>{s.hosting_provider || '—'}</td>
                <td>{s.monthly_cost != null ? `${s.monthly_cost} €` : '—'}</td>
                <td><ScoreChip score={s.last_score} /></td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Modifier" onClick={() => openModal({ type: 'site', data: s })}>✏️</button>
                    {editMode && isSuperAdmin && <button className="btn-icon delete" title="Supprimer" onClick={() => del(s)}>🗑️</button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="mh-empty">Aucun site enregistré.</td></tr>}
          </tbody>
        </table>
      </div>
      {profile && <SiteProfile site={profile} onClose={() => setProfile(null)} openModal={openModal} isSuperAdmin={isSuperAdmin} />}
    </>
  );
}

function SiteProfile({ site, onClose, openModal, isSuperAdmin }) {
  const [runs, setRuns] = useState([]);
  const [costs, setCosts] = useState([]);
  const [actions, setActions] = useState([]);
  const loadActions = useCallback(() => { mhApi.listSiteActions(site.id).then(r => setActions(r.data)).catch(() => {}); }, [site.id]);
  useEffect(() => {
    mhApi.siteRuns(site.id).then(r => setRuns(r.data)).catch(() => {});
    mhApi.getCosts(site.id).then(r => setCosts(r.data)).catch(() => {});
    loadActions();
  }, [site.id, loadActions]);
  const delAction = async (a) => {
    if (!window.confirm(`Supprimer l'action « ${a.title} » ?`)) return;
    try { await mhApi.deleteAction(a.id); loadActions(); } catch { alert('Suppression impossible'); }
  };
  const ts = site.tech_stack || {};
  return (
    <div className="mh-drawer-overlay" onClick={onClose}>
      <div className="mh-drawer" onClick={e => e.stopPropagation()}>
        <div className="mh-drawer-head">
          <h2>{site.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mh-drawer-body">
          <a href={site.url} target="_blank" rel="noreferrer" className="mh-link">{site.url} ↗</a>
          <div className="mh-kv">
            <div><span>Type</span>{SITE_TYPES.find(t => t.value === site.site_type)?.label || site.site_type}</div>
            <div><span>Zone</span>{site.zone || '—'}</div>
            <div><span>Marques</span>{(site.brands || []).join(', ') || '—'}</div>
            <div><span>Mots-clés</span>{(site.target_keywords || []).join(', ') || '—'}</div>
            <div><span>Gestionnaire</span>{site.manager_type === 'provider' ? `${site.provider_name || ''} ${site.provider_contact ? `(${site.provider_contact})` : ''}` : 'Interne'}</div>
            <div><span>Hébergeur</span>{site.hosting_provider || '—'} {ts.cms ? `· ${ts.cms}` : ''}</div>
            <div><span>Google Analytics (GA4)</span>{site.ga4_property_id || '—'}{site.ga4_manager_provider ? ` · 👤 ${site.ga4_manager_provider}` : ''}</div>
            <div><span>Google Ads</span>{site.ads_account_id || '—'}{site.ads_manager_provider ? ` · 👤 ${site.ads_manager_provider}` : ''}</div>
            <div><span>Search Console</span>{site.gsc_property || '—'}{site.gsc_manager_provider ? ` · 👤 ${site.gsc_manager_provider}` : ''}</div>
            <div><span>Business Profile (GMB)</span>{site.gmb_location_id || '—'}{site.gmb_manager_provider ? ` · 👤 ${site.gmb_manager_provider}` : ''}</div>
          </div>

          <h3>Historique d'audit</h3>
          {runs.length ? (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={runs.slice().reverse().map(r => ({ d: fmtDate(r.run_at), score: r.global_score }))}>
                <XAxis dataKey="d" tick={{ fontSize: 10 }} /><YAxis domain={[0, 10]} tick={{ fontSize: 10 }} width={24} />
                <Tooltip /><Line type="monotone" dataKey="score" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="mh-muted">Aucun audit encore.</p>}

          <h3 className="mh-journal-head">
            Journal d'actions
            {openModal && <button className="mh-link" onClick={() => openModal({ type: 'action', siteId: site.id, onSaved: loadActions })}>+ Action</button>}
          </h3>
          {actions.length ? (
            <table className="mh-mini-table"><tbody>
              {actions.map(a => (
                <tr key={a.id} className="mh-row" onClick={() => openModal && openModal({ type: 'action', data: a, siteId: site.id, onSaved: loadActions })}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(a.action_date)}</td>
                  <td><span className="mh-action-badge" style={{ color: (ACTION_CATS[a.category] || {}).color }}>{(ACTION_CATS[a.category] || {}).emoji} {(ACTION_CATS[a.category] || {}).label || a.category}</span></td>
                  <td>{a.title}<span className="mh-action-chan"> · {(a.channels || []).join(', ')}</span></td>
                  {isSuperAdmin && <td onClick={e => e.stopPropagation()}><button className="btn-icon delete" title="Supprimer" onClick={() => delAction(a)}>🗑️</button></td>}
                </tr>
              ))}
            </tbody></table>
          ) : <p className="mh-muted">Aucune action enregistrée.</p>}

          <h3>Coûts</h3>
          {costs.length ? (
            <table className="mh-mini-table"><tbody>
              {costs.map(c => <tr key={c.id}><td>{fmtDate(c.period_month)}</td><td>{c.category}</td><td>{c.amount} {c.currency}</td></tr>)}
            </tbody></table>
          ) : <p className="mh-muted">Aucun coût saisi.</p>}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════ Journal d'actions ════════════════════════ */

const ACTION_CATS = {
  refonte:     { label: 'Refonte',     emoji: '🏗️', color: 'var(--color-accent)' },
  contenu_seo: { label: 'Contenu SEO', emoji: '✍️', color: 'var(--color-success)' },
  campagne:    { label: 'Campagne',    emoji: '📣', color: 'var(--color-info)' },
  technique:   { label: 'Technique',   emoji: '🔧', color: 'var(--color-warning)' },
  tracking:    { label: 'Tracking',    emoji: '🎯', color: 'var(--color-decision)' },
  netlinking:  { label: 'Netlinking',  emoji: '🔗', color: 'var(--text-secondary)' },
  autre:       { label: 'Autre',       emoji: '📌', color: 'var(--text-muted)' },
};
const CHANNEL_OPTS = [
  { key: 'tous', label: 'Tous' }, { key: 'ga4', label: 'Analytics' }, { key: 'ads', label: 'Ads' },
  { key: 'gsc', label: 'Search Console' }, { key: 'gmb', label: 'Business Profile' },
];

function ActionsView({ sites, isSuperAdmin, editMode, openModal, reloadKey }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [fCat, setFCat] = useState('all');
  const [fSite, setFSite] = useState('all');
  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (fCat !== 'all') params.category = fCat;
    if (fSite !== 'all') params.site_id = fSite;
    mhApi.listAllActions(params).then(r => setActions(r.data)).catch(() => setActions([])).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fCat, fSite, reloadKey]); // reloadKey force le rechargement après création via le header
  useEffect(() => { load(); }, [load]);
  const del = async (a) => { if (!window.confirm(`Supprimer « ${a.title} » ?`)) return; try { await mhApi.deleteAction(a.id); load(); } catch { alert('Suppression impossible'); } };
  const term = q.trim().toLowerCase();
  const rows = actions.filter(a => !term || (a.title || '').toLowerCase().includes(term) || (a.site_name || '').toLowerCase().includes(term) || (a.description || '').toLowerCase().includes(term));
  const canDelete = editMode && isSuperAdmin;

  return (
    <div>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={q} onChange={e => setQ(e.target.value)} />
        <select className={`page-select${fCat !== 'all' ? ' active-filter' : ''}`} value={fCat} onChange={e => setFCat(e.target.value)}>
          <option value="all">Toutes catégories</option>
          {Object.entries(ACTION_CATS).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
        </select>
        <select className={`page-select${fSite !== 'all' ? ' active-filter' : ''}`} value={fSite} onChange={e => setFSite(e.target.value)}>
          <option value="all">Tous les sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="table-container">
        <table>
          <thead><tr><th>Date</th><th>Site</th><th>Catégorie</th><th>Canaux</th><th>Action</th>{canDelete && <th></th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="mh-muted" style={{ textAlign: 'center', padding: 24 }}>Chargement…</td></tr>}
            {!loading && !rows.length && <tr><td colSpan={6} className="mh-muted" style={{ textAlign: 'center', padding: 24 }}>Aucune action — cliquez « + Action ».</td></tr>}
            {rows.map(a => {
              const c = ACTION_CATS[a.category] || {};
              return (
                <tr key={a.id} className="mh-row" onClick={() => openModal({ type: 'action', data: a, siteId: a.site_id, onSaved: load })}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(a.action_date)}</td>
                  <td>{a.site_name}</td>
                  <td><span className="mh-action-badge" style={{ color: c.color }}>{c.emoji} {c.label || a.category}</span></td>
                  <td className="mh-action-chan">{(a.channels || []).join(', ')}</td>
                  <td>{a.title}{a.expected_impact ? <span className="mh-muted"> — {a.expected_impact}</span> : ''}</td>
                  {canDelete && <td onClick={e => e.stopPropagation()}><button className="btn-icon delete" title="Supprimer" onClick={() => del(a)}>🗑️</button></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActionModal({ action, presetSiteId, sites = [], onClose, onSaved }) {
  const isEdit = !!action;
  const [form, setForm] = useState({
    site_id: action?.site_id || presetSiteId || '',
    action_date: action?.action_date ? String(action.action_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
    category: action?.category || 'refonte',
    channels: action?.channels || ['tous'],
    title: action?.title || '',
    description: action?.description || '',
    expected_impact: action?.expected_impact || '',
    url_affected: action?.url_affected || '',
    status: action?.status || 'done',
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const toggleChan = (c) => setForm(f => {
    if (c === 'tous') return { ...f, channels: ['tous'] };
    let ch = f.channels.filter(x => x !== 'tous');
    ch = ch.includes(c) ? ch.filter(x => x !== c) : [...ch, c];
    return { ...f, channels: ch.length ? ch : ['tous'] };
  });
  const canSave = form.site_id && form.action_date && form.title.trim();
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (isEdit) await mhApi.updateAction(action.id, form);
      else await mhApi.addSiteAction(form.site_id, form);
      onSaved && onSaved();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{isEdit ? "✏️ Modifier l'action" : '🗓️ Nouvelle action'}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="mh-form-grid">
            <label>Site *
              <select value={form.site_id} onChange={e => set({ site_id: e.target.value })} disabled={!!presetSiteId && !isEdit}>
                <option value="">— Choisir —</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label>Date *
              <input type="date" value={form.action_date} onChange={e => set({ action_date: e.target.value })} />
            </label>

            <div className="mh-form-section">Catégorie</div>
            <div className="mh-cat-grid">
              {Object.entries(ACTION_CATS).map(([k, v]) => (
                <button key={k} type="button" className={`mh-cat-chip${form.category === k ? ' active' : ''}`}
                  style={{ '--_c': v.color }} onClick={() => set({ category: k })}>
                  <span className="mh-cat-emoji">{v.emoji}</span>{v.label}
                </button>
              ))}
            </div>

            <div className="mh-form-section">Canaux concernés <span className="mh-form-hint">où afficher le repère sur les courbes</span></div>
            <div className="mh-chan-picker">
              {CHANNEL_OPTS.map(c => (
                <button key={c.key} type="button" className={`filter-chip${form.channels.includes(c.key) ? ' active' : ''}`} onClick={() => toggleChan(c.key)}>{c.label}</button>
              ))}
            </div>

            <label className="mh-col2">Titre *
              <input type="text" placeholder="ex. Refonte de la page d'accueil" value={form.title} onChange={e => set({ title: e.target.value })} />
            </label>
            <label className="mh-col2">Description
              <textarea value={form.description} onChange={e => set({ description: e.target.value })} />
            </label>
            <label>Impact attendu
              <input type="text" placeholder="ex. hausse trafic organique" value={form.expected_impact} onChange={e => set({ expected_impact: e.target.value })} />
            </label>
            <label>Statut
              <select value={form.status} onChange={e => set({ status: e.target.value })}>
                <option value="done">Réalisée</option><option value="in_progress">En cours</option><option value="planned">Planifiée</option>
              </select>
            </label>
            <label className="mh-col2">Page(s) concernée(s)
              <input type="text" placeholder="URL (optionnel)" value={form.url_affected} onChange={e => set({ url_affected: e.target.value })} />
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save} disabled={!canSave || saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}

function SiteModal({ site, clients = [], onClose, onSaved }) {
  const [f, setF] = useState({
    name: '', url: '', site_type: 'vitrine', annonce_subtype: '', zone: '', client_id: '',
    manager_type: 'internal', provider_name: '', provider_contact: '',
    ga4_property_id: '', ga4_manager_provider: '',
    ads_account_id: '', ads_manager_provider: '',
    gsc_property: '', gsc_manager_provider: '',
    gmb_location_id: '', gmb_manager_provider: '',
    inventory_url: '', hosting_provider: '', monthly_cost: '',
    active: true, audit_enabled: true, ...site,
    target_keywords: (site?.target_keywords || []).join(', '),
    brands: (site?.brands || []).join(', '),
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name || !f.url) { alert('Nom et URL requis'); return; }
    const payload = {
      ...f,
      client_id: f.client_id || null,
      monthly_cost: f.monthly_cost === '' ? null : Number(f.monthly_cost),
      // Un identifiant renseigné = compte existant (plus de cases à cocher).
      ads_exists: !!(f.ads_account_id && f.ads_account_id.trim()),
      gsc_exists: !!(f.gsc_property && f.gsc_property.trim()),
      gmb_exists: !!(f.gmb_location_id && f.gmb_location_id.trim()),
      target_keywords: String(f.target_keywords).split(',').map(s => s.trim()).filter(Boolean),
      brands: String(f.brands).split(',').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (site?.id) await mhApi.updateSite(site.id, payload);
      else await mhApi.createSite(payload);
      onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{site?.id ? 'Modifier' : 'Nouveau'} site</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="mh-form-grid">
            <h4 className="mh-form-section">Identité</h4>
            <label>Nom<input value={f.name} onChange={e => set('name', e.target.value)} /></label>
            <label>URL<input value={f.url} onChange={e => set('url', e.target.value)} placeholder="https://..." /></label>
            <label>Type
              <select value={f.site_type} onChange={e => set('site_type', e.target.value)}>
                {SITE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            {f.site_type === 'annonces' ? (
              <label>Sous-type
                <select value={f.annonce_subtype || ''} onChange={e => set('annonce_subtype', e.target.value)}>
                  <option value="">—</option><option value="emploi">Emploi</option><option value="immobilier">Immobilier</option>
                </select>
              </label>
            ) : <label>Zone / DOM<input value={f.zone || ''} onChange={e => set('zone', e.target.value)} placeholder="Martinique, Guadeloupe..." /></label>}
            {f.site_type === 'annonces' && <label>Zone / DOM<input value={f.zone || ''} onChange={e => set('zone', e.target.value)} placeholder="Martinique, Guadeloupe..." /></label>}
            <label className="mh-col2">Marques (séparées par ,)<input value={f.brands} onChange={e => set('brands', e.target.value)} /></label>
            <label className="mh-col2">Mots-clés cibles (séparés par ,)<input value={f.target_keywords} onChange={e => set('target_keywords', e.target.value)} /></label>
            <label className="mh-col2">URL page stock / annonces<input value={f.inventory_url || ''} onChange={e => set('inventory_url', e.target.value)} placeholder="https://...  (catalogue VO, liste d'annonces…)" /></label>

            <h4 className="mh-form-section">Gestion du site</h4>
            <label>Client<select value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}><option value="">—</option>{clients.filter(c => c.active || String(c.id) === String(f.client_id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
            <label>Gestionnaire du site
              <select value={f.manager_type} onChange={e => set('manager_type', e.target.value)}>
                <option value="internal">Interne</option><option value="provider">Prestataire</option>
              </select>
            </label>
            {f.manager_type === 'provider' && <label>Nom prestataire<input value={f.provider_name || ''} onChange={e => set('provider_name', e.target.value)} /></label>}
            {f.manager_type === 'provider' && <label>Contact prestataire<input value={f.provider_contact || ''} onChange={e => set('provider_contact', e.target.value)} placeholder="email / téléphone" /></label>}
            <label>Hébergeur <span className="mh-form-hint">(auto-détecté à l'audit)</span><input value={f.hosting_provider || ''} onChange={e => set('hosting_provider', e.target.value)} /></label>
            <label>Coût mensuel (€)<input type="number" value={f.monthly_cost ?? ''} onChange={e => set('monthly_cost', e.target.value)} /></label>

            <h4 className="mh-form-section">Comptes &amp; gestionnaires <span className="mh-form-hint">un identifiant renseigné = compte existant ; laisser vide = pas de compte</span></h4>
            <label>Google Analytics — ID propriété GA4<input value={f.ga4_property_id || ''} onChange={e => set('ga4_property_id', e.target.value)} placeholder="ex. 489656478" /></label>
            <label>↳ Gestionnaire GA4<input value={f.ga4_manager_provider || ''} onChange={e => set('ga4_manager_provider', e.target.value)} placeholder="nom interne ou agence" /></label>
            <label>Google Ads — ID compte<input value={f.ads_account_id || ''} onChange={e => set('ads_account_id', e.target.value)} placeholder="123-456-7890" /></label>
            <label>↳ Gestionnaire Google Ads<input value={f.ads_manager_provider || ''} onChange={e => set('ads_manager_provider', e.target.value)} placeholder="nom interne ou agence" /></label>
            <label>Search Console — propriété<input value={f.gsc_property || ''} onChange={e => set('gsc_property', e.target.value)} placeholder="sc-domain:exemple.fr" /></label>
            <label>↳ Gestionnaire Search Console<input value={f.gsc_manager_provider || ''} onChange={e => set('gsc_manager_provider', e.target.value)} placeholder="nom interne ou agence" /></label>
            <label>Google Business Profile — fiche (ID ou nom)<input value={f.gmb_location_id || ''} onChange={e => set('gmb_location_id', e.target.value)} placeholder="ID établissement ou nom de la fiche" /></label>
            <label>↳ Gestionnaire Business Profile<input value={f.gmb_manager_provider || ''} onChange={e => set('gmb_manager_provider', e.target.value)} placeholder="nom interne ou agence" /></label>

            <h4 className="mh-form-section">Options</h4>
            <label className="mh-check"><input type="checkbox" checked={!!f.audit_enabled} onChange={e => set('audit_enabled', e.target.checked)} /> Audit automatique hebdomadaire</label>
            <label className="mh-check"><input type="checkbox" checked={!!f.active} onChange={e => set('active', e.target.checked)} /> Site actif</label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════ Projets (kanban) ════════════════════════════ */

function ProjectsView({ projects, sites, clients = [], members = [], reload, isSuperAdmin, openModal }) {
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState('kanban');     // 'kanban' | 'list'
  const [search, setSearch] = useState('');
  const [fOwner, setFOwner] = useState('');       // '' = tous, '__none__' = sans responsable, sinon id
  const [fClient, setFClient] = useState('');     // idem côté client
  const [sort, setSort] = useState({ key: 'due_date', dir: 'asc' });
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [collapsedCols, setCollapsedCols] = useState(() => new Set(['done']));
  const toggleColCollapse = (key) => setCollapsedCols(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });
  const move = async (p, status) => { try { await mhApi.moveProject(p.id, { status }); reload(); } catch { /* */ } };

  const onDrop = (status) => {
    const p = projects.find(x => x.id === dragId);
    setDragId(null); setOverCol(null);
    if (p && p.status !== status) move(p, status);
  };

  // Filtres responsable + client + recherche, appliqués aux deux vues.
  const filtered = useMemo(() => projects.filter(p => {
    if (fOwner === '__none__' ? p.owner_user_id : fOwner && String(p.owner_user_id || '') !== String(fOwner)) return false;
    if (fClient === '__none__' ? p.client_id : fClient && String(p.client_id || '') !== String(fClient)) return false;
    if (search && !`${p.title || ''} ${p.client_nom || ''} ${p.owner_nom || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [projects, fOwner, fClient, search]);

  const cmp = (a, b) => {
    let va, vb;
    switch (sort.key) {
      case 'client':   va = (a.client_nom || '').toLowerCase(); vb = (b.client_nom || '').toLowerCase(); break;
      case 'owner':    va = (a.owner_nom || '').toLowerCase();  vb = (b.owner_nom || '').toLowerCase();  break;
      case 'status':   va = STATUS_ORDER[a.status] ?? -1;       vb = STATUS_ORDER[b.status] ?? -1;       break;
      case 'priority': va = PRIORITY_ORDER[a.priority] ?? -1;   vb = PRIORITY_ORDER[b.priority] ?? -1;   break;
      case 'tasks':    va = Number(a.task_count || 0);          vb = Number(b.task_count || 0);          break;
      case 'time':     va = Number(a.time_seconds || 0);        vb = Number(b.time_seconds || 0);        break;
      case 'due_date': va = a.due_date ? new Date(a.due_date).getTime() : null; vb = b.due_date ? new Date(b.due_date).getTime() : null; break;
      default:         va = (a.title || '').toLowerCase();      vb = (b.title || '').toLowerCase();
    }
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const r = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === 'asc' ? r : -r;
  };
  const sorted = [...filtered].sort(cmp);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const toolbar = (
    <div className="page-toolbar">
      <input className="page-search" placeholder="🔍 Rechercher un projet..." value={search} onChange={e => setSearch(e.target.value)} />
      <select className={`page-select mh-filter-select ${fOwner ? 'active-filter' : ''}`} value={fOwner} onChange={e => setFOwner(e.target.value)}>
        <option value="">Tous les responsables</option>
        <option value="__none__">— Sans responsable</option>
        {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
      </select>
      <select className={`page-select mh-filter-select ${fClient ? 'active-filter' : ''}`} value={fClient} onChange={e => setFClient(e.target.value)}>
        <option value="">Tous les clients</option>
        <option value="__none__">— Sans client</option>
        {clients.filter(c => c.active || String(c.id) === String(fClient)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <div className="mh-seg">
        <button className={view === 'kanban' ? 'active' : ''} onClick={() => setView('kanban')}>▦ Kanban</button>
        <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>☰ Liste</button>
        <button className={view === 'workload' ? 'active' : ''} onClick={() => setView('workload')}>📅 Charge</button>
      </div>
      <span className="mh-count">{filtered.length} projet(s)</span>
    </div>
  );

  if (view === 'workload') {
    return (
      <>
        {toolbar}
        <WorkloadView projects={filtered} onOpen={setDetail} />
        {detail && <ProjectDetail project={detail} onClose={() => setDetail(null)} reload={reload} isSuperAdmin={isSuperAdmin} members={members} openModal={openModal} />}
      </>
    );
  }

  if (view === 'list') {
    return (
      <>
        {toolbar}
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <SortTh label="Projet" col="title" sort={sort} setSort={setSort} />
                <th>Équipe</th>
                <SortTh label="Client" col="client" sort={sort} setSort={setSort} />
                <SortTh label="Responsable" col="owner" sort={sort} setSort={setSort} />
                <SortTh label="Statut" col="status" sort={sort} setSort={setSort} />
                <SortTh label="Priorité" col="priority" sort={sort} setSort={setSort} />
                <SortTh label="Échéance" col="due_date" sort={sort} setSort={setSort} />
                <SortTh label="Tâches" col="tasks" sort={sort} setSort={setSort} />
                <SortTh label="Temps" col="time" sort={sort} setSort={setSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map(p => {
                const overdue = p.due_date && p.status !== 'done' && new Date(p.due_date) < today;
                return (
                  <tr key={p.id} onClick={() => setDetail(p)} style={{ cursor: 'pointer' }}>
                    <td><span className={`mh-team-dot ${p.team}`} />{p.title}</td>
                    <td>{p.team === 'digital' ? 'Digital' : 'Marketing'}</td>
                    <td>{p.client_nom || '—'}</td>
                    <td>{p.owner_nom || '—'}</td>
                    <td>{STATUS_LABELS[p.status] || p.status}</td>
                    <td>{p.priority === 'urgent' ? <span className="status-error mh-prio">Urgent</span> : (PRIORITY_LABELS[p.priority] || '—')}</td>
                    <td className={overdue ? 'mh-overdue' : ''}>{fmtDate(p.due_date)}</td>
                    <td>{p.task_done}/{p.task_count} ✓</td>
                    <td>{fmtDuration(p.time_seconds)}</td>
                  </tr>
                );
              })}
              {sorted.length === 0 && <tr><td colSpan={9} className="mh-muted" style={{ textAlign: 'center', padding: 20 }}>Aucun projet.</td></tr>}
            </tbody>
          </table>
        </div>
        {detail && <ProjectDetail project={detail} onClose={() => setDetail(null)} reload={reload} isSuperAdmin={isSuperAdmin} members={members} openModal={openModal} />}
      </>
    );
  }

  return (
    <>
      {toolbar}
      <div className="mh-kanban">
        {KANBAN_COLS.map(col => {
          const items = filtered.filter(p => p.status === col.key);
          const collapsed = collapsedCols.has(col.key);
          return (
            <div
              key={col.key}
              className={`mh-kcol${overCol === col.key && dragId && !collapsed ? ' drag-over' : ''}${collapsed ? ' collapsed' : ''}`}
              onDragOver={!collapsed ? e => { if (dragId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverCol(col.key); } } : undefined}
              onDragLeave={!collapsed ? e => { if (!e.currentTarget.contains(e.relatedTarget)) setOverCol(c => (c === col.key ? null : c)); } : undefined}
              onDrop={!collapsed ? e => { e.preventDefault(); onDrop(col.key); } : undefined}
              onClick={collapsed ? () => toggleColCollapse(col.key) : undefined}
            >
              <div className="mh-kcol-head" onClick={!collapsed ? () => toggleColCollapse(col.key) : undefined} title={collapsed ? 'Déplier' : 'Replier'}>
                <span className={`mh-kcol-dot ${col.key}`} />
                <span className="mh-kcol-head-label">{col.label}</span>
                <span className="mh-kcol-count">{items.length}</span>
                {!collapsed && (
                  <span className="mh-col-collapse-icon">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                  </span>
                )}
              </div>
              {!collapsed && items.map(p => {
                const taskPct = p.task_count > 0 ? Math.round((p.task_done / p.task_count) * 100) : 0;
                const overdue = p.due_date && col.key !== 'done' && new Date(p.due_date) < today;
                return (
                  <div
                    key={p.id}
                    className={`mh-card ${dragId === p.id ? 'dragging' : ''}`}
                    draggable
                    onDragStart={e => { setDragId(p.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(p.id)); }}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    onClick={() => setDetail(p)}
                  >
                    {p.priority && p.priority !== 'normal' && (
                      <div className="mh-prio-row">
                        <span className={`mh-prio-badge mh-prio-${p.priority}`}>{PRIORITY_LABELS[p.priority]}</span>
                      </div>
                    )}
                    <div className="mh-card-top">
                      <span className={`mh-team-dot ${p.team}`} />
                      <span className="mh-card-title">{p.title}</span>
                    </div>
                    {p.client_nom && <div className="mh-card-site">👥 {p.client_nom}</div>}
                    {p.site_nom && <div className="mh-card-site">🛰️ {p.site_nom}</div>}
                    {p.task_count > 0 && (
                      <div className="mh-card-progress">
                        <div className="mh-card-progress-track">
                          <div className="mh-card-progress-fill" style={{ width: `${taskPct}%` }} />
                        </div>
                        <span className="mh-card-progress-label">{p.task_done}/{p.task_count} tâches</span>
                      </div>
                    )}
                    <div className="mh-card-meta">
                      {p.owner_nom && (
                        <span className="mh-card-owner-chip" title={p.owner_nom}>
                          {p.owner_nom.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase()}
                        </span>
                      )}
                      {p.due_date && (
                        <span className={`mh-card-due${overdue ? ' overdue' : ''}`}>{fmtDate(p.due_date)}</span>
                      )}
                      {p.time_seconds > 0 && (
                        <span className="mh-card-time">{fmtDuration(p.time_seconds)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {!collapsed && items.length === 0 && <div className="mh-kcol-empty">{dragId ? 'Déposer ici' : '—'}</div>}
            </div>
          );
        })}
      </div>
      {detail && <ProjectDetail project={detail} onClose={() => setDetail(null)} reload={reload} isSuperAdmin={isSuperAdmin} members={members} openModal={openModal} />}
    </>
  );
}

// Vue « Charge » : synthèse de la charge par responsable + calendrier des échéances.
// Au niveau projet (responsable = owner), à partir des projets déjà chargés (filtrés).
function WorkloadView({ projects, onOpen }) {
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const workload = useMemo(() => {
    const m = new Map();
    projects.forEach(p => {
      const key = p.owner_user_id || '__none__';
      if (!m.has(key)) m.set(key, { name: p.owner_nom || 'Non assigné', active: 0, overdue: 0, time: 0, next: null, total: 0 });
      const w = m.get(key);
      w.total += 1;
      w.time += Number(p.time_seconds || 0);
      if (p.status !== 'done') {
        w.active += 1;
        if (p.due_date) {
          const due = new Date(p.due_date); due.setHours(0, 0, 0, 0);
          if (due < today) w.overdue += 1;
          if (due >= today && (!w.next || due < w.next)) w.next = due;
        }
      }
    });
    return [...m.values()].sort((a, b) => b.active - a.active || b.total - a.total);
  }, [projects, today]);
  const maxActive = Math.max(1, ...workload.map(w => w.active));

  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7; // semaine commençant le lundi
    const start = new Date(first); start.setDate(first.getDate() - startOffset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); d.setHours(0, 0, 0, 0); return d; });
  }, [cursor]);
  const byDay = useMemo(() => {
    const m = {};
    projects.forEach(p => { if (!p.due_date) return; const d = new Date(p.due_date); d.setHours(0, 0, 0, 0); (m[d.getTime()] = m[d.getTime()] || []).push(p); });
    return m;
  }, [projects]);

  const WD = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const shiftMonth = (n) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + n, 1));

  return (
    <div className="mh-workload">
      <section className="mh-panel">
        <h3>Charge par responsable</h3>
        <table className="mh-mini-table mh-load-table">
          <thead><tr><th>Responsable</th><th>Projets actifs</th><th>En retard</th><th>Prochaine échéance</th><th>Temps cumulé</th></tr></thead>
          <tbody>
            {workload.map((w, i) => (
              <tr key={i}>
                <td>{w.name}</td>
                <td><div className="mh-load-cell"><div className="mh-load-bar"><span style={{ width: `${(w.active / maxActive) * 100}%` }} /></div><span className="mh-load-num">{w.active}</span></div></td>
                <td className={w.overdue ? 'mh-overdue' : ''}>{w.overdue || '—'}</td>
                <td>{w.next ? fmtDate(w.next) : '—'}</td>
                <td>{fmtDuration(w.time)}</td>
              </tr>
            ))}
            {workload.length === 0 && <tr><td colSpan={5} className="mh-muted" style={{ textAlign: 'center', padding: 16 }}>Aucun projet.</td></tr>}
          </tbody>
        </table>
      </section>

      <section className="mh-panel">
        <div className="mh-cal-head">
          <h3 style={{ textTransform: 'capitalize' }}>Échéances — {monthLabel}</h3>
          <div className="mh-cal-nav">
            <button className="mh-move-btn" title="Mois précédent" onClick={() => shiftMonth(-1)}>‹</button>
            <button className="mh-move-btn" onClick={() => setCursor(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); })}>Aujourd'hui</button>
            <button className="mh-move-btn" title="Mois suivant" onClick={() => shiftMonth(1)}>›</button>
          </div>
        </div>
        <div className="mh-cal-grid">
          {WD.map(d => <div key={d} className="mh-cal-wd">{d}</div>)}
          {cells.map((d, i) => {
            const evs = byDay[d.getTime()] || [];
            const out = d.getMonth() !== cursor.getMonth();
            const isToday = d.getTime() === today.getTime();
            return (
              <div key={i} className={`mh-cal-cell ${out ? 'out' : ''} ${isToday ? 'today' : ''}`}>
                <div className="mh-cal-daynum">{d.getDate()}</div>
                {evs.map(p => {
                  const overdue = p.status !== 'done' && d < today;
                  return (
                    <button key={p.id} className={`mh-cal-ev ${p.team} ${overdue ? 'overdue' : ''} ${p.status === 'done' ? 'done' : ''}`} title={`${p.title}${p.owner_nom ? ' — ' + p.owner_nom : ''}`} onClick={() => onOpen(p)}>
                      {p.title}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function ProjectDetail({ project, onClose, reload, isSuperAdmin, members = [], openModal }) {
  const [data, setData] = useState(null);
  const [newTask, setNewTask] = useState('');
  const [newTaskDate, setNewTaskDate] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [taskView, setTaskView] = useState('list');  // 'list' | 'kanban' | 'planning'
  const [dragTask, setDragTask] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const load = useCallback(() => mhApi.getProject(project.id).then(r => setData(r.data)).catch(() => {}), [project.id]);
  useEffect(() => { load(); }, [load]);
  const addTask = async () => {
    if (!newTask.trim()) return;
    const payload = { title: newTask };
    if (newTaskDate) payload.due_date = newTaskDate;
    if (newTaskAssignee) payload.assignee_user_id = parseInt(newTaskAssignee);
    await mhApi.addTask(project.id, payload);
    setNewTask(''); setNewTaskDate(''); setNewTaskAssignee('');
    load(); reload();
  };
  const toggle = async (t) => { await mhApi.toggleTask(t.id); load(); reload(); };
  // Déplacement kanban d'une tâche : on synchronise aussi le flag « done ».
  const moveTask = async (t, status) => { if (t.status === status) return; try { await mhApi.updateTask(t.id, { status, done: status === 'done' }); load(); reload(); } catch { /* */ } };
  const assignTask = async (t, uid) => { try { await mhApi.updateTask(t.id, { assignee_user_id: uid || null }); load(); } catch { /* */ } };
  const delProject = async () => { if (!window.confirm('Supprimer ce projet ?')) return; await mhApi.deleteProject(project.id); onClose(); reload(); };

  const tasks = data?.tasks || [];
  const onDropTask = (status) => {
    const t = tasks.find(x => x.id === dragTask);
    setDragTask(null); setOverCol(null);
    if (t) moveTask(t, status);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{project.title}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {data?.description && <p className="mh-muted">{data.description}</p>}
          <div className="mh-kv">
            <div><span>Équipe</span>{project.team === 'digital' ? 'Digital' : 'Marketing'}</div>
            <div><span>Responsable</span>{project.owner_nom || '—'}</div>
            {project.client_nom && <div><span>Client</span>{project.client_nom}</div>}
            <div><span>Priorité</span>{PRIORITY_LABELS[project.priority] || '—'}</div>
            <div><span>Échéance</span>{fmtDate(project.due_date)}</div>
            <div><span>Temps cumulé</span>{fmtDuration(project.time_seconds)}</div>
          </div>

          <div className="mh-tasks-head">
            <h3>Tâches {tasks.length > 0 && <span className="mh-muted">({tasks.filter(t => t.done).length}/{tasks.length})</span>}</h3>
            <div className="mh-seg">
              <button className={taskView === 'list' ? 'active' : ''} onClick={() => setTaskView('list')}>☰ Liste</button>
              <button className={taskView === 'kanban' ? 'active' : ''} onClick={() => setTaskView('kanban')}>▦ Kanban</button>
              <button className={taskView === 'planning' ? 'active' : ''} onClick={() => setTaskView('planning')}>📅 Planning</button>
            </div>
          </div>

          {taskView === 'list' && (
            <ul className="mh-tasks">
              {tasks.map(t => (
                <li key={t.id}>
                  <label><input type="checkbox" checked={t.done} onChange={() => toggle(t)} /> <span className={t.done ? 'mh-done' : ''}>{t.title}</span></label>
                  {t.source_finding_id && <span className="status-info mh-tag">audit</span>}
                  {t.due_date && <span className={`mh-card-due${!t.done && new Date(t.due_date) < new Date() ? ' overdue' : ''}`}>📅 {fmtDate(t.due_date)}</span>}
                  <select className="mh-mini-select" value={t.assignee_user_id || ''} onChange={e => assignTask(t, e.target.value)}>
                    <option value="">Non assigné</option>
                    {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                  </select>
                </li>
              ))}
              {tasks.length === 0 && <li className="mh-muted">Aucune tâche.</li>}
            </ul>
          )}

          {taskView === 'kanban' && (
            <div className="mh-kanban mh-kanban-tasks">
              {TASK_KANBAN_COLS.map(col => {
                const items = tasks.filter(t => (t.status || 'todo') === col.key);
                return (
                  <div
                    key={col.key}
                    className={`mh-kcol ${overCol === col.key && dragTask ? 'drag-over' : ''}`}
                    onDragOver={e => { if (dragTask) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverCol(col.key); } }}
                    onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOverCol(c => (c === col.key ? null : c)); }}
                    onDrop={e => { e.preventDefault(); onDropTask(col.key); }}
                  >
                    <div className="mh-kcol-head">
                      <span className={`mh-kcol-dot ${col.key}`} />
                      <span className="mh-kcol-head-label">{col.label}</span>
                      <span className="mh-kcol-count">{items.length}</span>
                    </div>
                    {items.map(t => (
                      <div
                        key={t.id}
                        className={`mh-card ${dragTask === t.id ? 'dragging' : ''}`}
                        draggable
                        onDragStart={e => { setDragTask(t.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(t.id)); }}
                        onDragEnd={() => { setDragTask(null); setOverCol(null); }}
                      >
                        <div className="mh-card-top"><span className={`mh-card-title${t.done ? ' mh-done' : ''}`}>{t.title}</span></div>
                        {t.source_finding_id && <span className="status-info mh-tag">audit</span>}
                        {t.due_date && <div className={`mh-card-due${!t.done && new Date(t.due_date) < new Date() ? ' overdue' : ''}`}>📅 {fmtDate(t.due_date)}</div>}
                        <div className="mh-card-meta">
                          <select className="mh-mini-select" value={t.assignee_user_id || ''} onClick={e => e.stopPropagation()} onChange={e => assignTask(t, e.target.value)}>
                            <option value="">Non assigné</option>
                            {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                          </select>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && <div className="mh-kcol-empty">{dragTask ? 'Déposer ici' : '—'}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {taskView === 'planning' && (() => {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const withDate = tasks.filter(t => t.due_date).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
            const noDate = tasks.filter(t => !t.due_date);
            const overdue = withDate.filter(t => !t.done && new Date(t.due_date) < today);
            const upcoming = withDate.filter(t => t.done || new Date(t.due_date) >= today);
            const PlanRow = ({ t }) => {
              const isOverdue = !t.done && new Date(t.due_date) < today;
              return (
                <li className="mh-tasks-plan-row">
                  <label><input type="checkbox" checked={t.done} onChange={() => toggle(t)} /> <span className={t.done ? 'mh-done' : ''}>{t.title}</span></label>
                  {t.source_finding_id && <span className="status-info mh-tag">audit</span>}
                  <span className={`mh-card-due${isOverdue ? ' overdue' : ''}`}>📅 {fmtDate(t.due_date)}</span>
                  <span className="mh-muted mh-plan-assignee">{t.assignee_nom || '—'}</span>
                </li>
              );
            };
            return (
              <div className="mh-planning">
                {overdue.length > 0 && <>
                  <div className="mh-plan-section mh-plan-overdue">⚠️ En retard ({overdue.length})</div>
                  <ul className="mh-tasks">{overdue.map(t => <PlanRow key={t.id} t={t} />)}</ul>
                </>}
                {upcoming.length > 0 && <>
                  <div className="mh-plan-section">📅 À venir ({upcoming.length})</div>
                  <ul className="mh-tasks">{upcoming.map(t => <PlanRow key={t.id} t={t} />)}</ul>
                </>}
                {noDate.length > 0 && <>
                  <div className="mh-plan-section mh-plan-nodate">Sans date ({noDate.length})</div>
                  <ul className="mh-tasks">{noDate.map(t => (
                    <li key={t.id} className="mh-tasks-plan-row">
                      <label><input type="checkbox" checked={t.done} onChange={() => toggle(t)} /> <span className={t.done ? 'mh-done' : ''}>{t.title}</span></label>
                      {t.source_finding_id && <span className="status-info mh-tag">audit</span>}
                      <span className="mh-muted mh-plan-assignee">{t.assignee_nom || '—'}</span>
                    </li>
                  ))}</ul></>}
                {tasks.length === 0 && <p className="mh-muted">Aucune tâche.</p>}
              </div>
            );
          })()}

          <div className="mh-addtask">
            <input className="page-search" placeholder="Nouvelle tâche..." value={newTask} onChange={e => setNewTask(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTask()} />
            <input type="date" className="mh-mini-date" value={newTaskDate} onChange={e => setNewTaskDate(e.target.value)} title="Date limite" />
            <select className="mh-mini-select mh-mini-select--form" value={newTaskAssignee} onChange={e => setNewTaskAssignee(e.target.value)}>
              <option value="">Assigné à…</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
            </select>
            <button className="btn-primary" onClick={addTask}>+ Ajouter</button>
          </div>
        </div>
        <div className="modal-footer">
          {isSuperAdmin && <button className="btn-secondary mh-danger" onClick={delProject}>Supprimer</button>}
          {openModal && <button className="btn-secondary" onClick={() => { onClose(); openModal({ type: 'project', data: project }); }}>✏️ Modifier</button>}
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function ProjectModal({ project, sites, clients = [], members = [], user, onClose, onSaved }) {
  const [f, setF] = useState({ title: '', description: '', team: 'marketing', priority: 'normal', status: 'todo', site_id: '', client_id: '', owner_user_id: user?.id || '', due_date: '', ...project });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.title) { alert('Titre requis'); return; }
    const payload = { ...f, site_id: f.site_id || null, client_id: f.client_id || null, owner_user_id: f.owner_user_id || null, due_date: f.due_date || null };
    try { if (project?.id) await mhApi.updateProject(project.id, payload); else await mhApi.createProject(payload); onSaved(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h2>{project?.id ? 'Modifier' : 'Nouveau'} projet</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div className="mh-form-grid">
            <label className="mh-col2">Titre<input value={f.title} onChange={e => set('title', e.target.value)} /></label>
            <label className="mh-col2">Description<textarea value={f.description || ''} onChange={e => set('description', e.target.value)} /></label>
            <label>Équipe<select value={f.team} onChange={e => set('team', e.target.value)}><option value="marketing">Marketing</option><option value="digital">Digital</option></select></label>
            <label>Priorité<select value={f.priority} onChange={e => set('priority', e.target.value)}><option value="low">Basse</option><option value="normal">Normale</option><option value="high">Haute</option><option value="urgent">Urgente</option></select></label>
            <label>Responsable<select value={f.owner_user_id || ''} onChange={e => set('owner_user_id', e.target.value)}><option value="">—</option>{members.map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}</select></label>
            <label>Client<select value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}><option value="">—</option>{clients.filter(c => c.active || String(c.id) === String(f.client_id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
            <label>Site lié<select value={f.site_id || ''} onChange={e => set('site_id', e.target.value)}><option value="">—</option>{sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
            <label>Échéance<input type="date" value={f.due_date ? String(f.due_date).slice(0, 10) : ''} onChange={e => set('due_date', e.target.value)} /></label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════ Pointage ════════════════════════════ */

function PointageView({ projects, user, isSuperAdmin }) {
  const [rows, setRows] = useState([]);
  const [manual, setManual] = useState({ project_id: '', date: '', hours: '', note: '' });
  const load = useCallback(() => {
    const from = new Date(); from.setDate(from.getDate() - 28);
    mhApi.timesheet({ from: from.toISOString().slice(0, 10) }).then(r => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addManual = async () => {
    if (!manual.date || !manual.hours) { alert('Date et durée requises'); return; }
    try {
      await mhApi.manualEntry({
        project_id: manual.project_id || null,
        started_at: `${manual.date}T09:00:00`,
        duration_seconds: Math.round(Number(manual.hours) * 3600),
        note: manual.note || null,
      });
      setManual({ project_id: '', date: '', hours: '', note: '' });
      load();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  // Agrégat par projet
  const byProject = useMemo(() => {
    const m = {};
    rows.forEach(r => { const k = r.projet || 'Sans projet'; m[k] = (m[k] || 0) + Number(r.total_seconds); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [rows]);
  const totalMkt = rows.filter(r => r.team === 'marketing').reduce((a, r) => a + Number(r.total_seconds), 0);
  const totalDig = rows.filter(r => r.team === 'digital').reduce((a, r) => a + Number(r.total_seconds), 0);

  return (
    <div className="mh-pointage">
      <div className="mh-cockpit-grid">
        <section className="mh-panel">
          <h3>Saisie manuelle</h3>
          <div className="mh-manual">
            <select className="page-select" value={manual.project_id} onChange={e => setManual({ ...manual, project_id: e.target.value })}>
              <option value="">Sans projet</option>{projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <input type="date" className="page-select" value={manual.date} onChange={e => setManual({ ...manual, date: e.target.value })} />
            <input type="number" step="0.25" className="page-select" placeholder="heures" value={manual.hours} onChange={e => setManual({ ...manual, hours: e.target.value })} />
            <input className="page-select" placeholder="note" value={manual.note} onChange={e => setManual({ ...manual, note: e.target.value })} />
            <button className="btn-primary" onClick={addManual}>+ Ajouter</button>
          </div>
        </section>
        <section className="mh-panel">
          <h3>Répartition (28 j)</h3>
          <div className="mh-split">
            <div>
              <strong>{fmtDuration(totalMkt)}</strong>
              <span><span className="mh-team-dot marketing" />Marketing</span>
            </div>
            <div>
              <strong>{fmtDuration(totalDig)}</strong>
              <span><span className="mh-team-dot digital" />Digital</span>
            </div>
          </div>
        </section>
      </div>

      <section className="mh-panel">
        <h3>Temps par projet (28 j)</h3>
        <div className="table-container">
          <table>
            <tbody>
              {byProject.map(([proj, sec]) => (
                <tr key={proj}>
                  <td>{proj}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtDuration(sec)}</td>
                </tr>
              ))}
              {byProject.length === 0 && <tr><td colSpan={2} className="mh-empty">Aucun temps pointé.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/* ════════════════════════════ Audits ════════════════════════════ */

function AuditsView({ audits, reload, reloadProjects, isSuperAdmin }) {
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState({});

  const runNow = async (siteId) => {
    // run_id du dernier audit terminé AVANT lancement → permet de détecter
    // l'arrivée d'un NOUVEAU run (gère aussi les ré-audits).
    const prevRunId = audits.find(a => a.site_id === siteId)?.run_id || null;
    setRunning(r => ({ ...r, [siteId]: true }));
    try {
      await mhApi.runNow(siteId);
      // L'audit tourne en fond (~45-60 s). On poll jusqu'à ce qu'un nouveau run
      // terminé apparaisse, avec un plafond de sécurité (~96 s).
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await mhApi.listAudits();
          const site = data.find(a => a.site_id === siteId);
          const ready = site && site.run_id && site.run_id !== prevRunId;
          if (ready || attempts >= 12) {
            clearInterval(poll);
            setRunning(r => ({ ...r, [siteId]: false }));
            reload();
          }
        } catch { /* on réessaiera au prochain tour */ }
      }, 8000);
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); setRunning(r => ({ ...r, [siteId]: false })); }
  };
  const openRun = async (runId) => { if (!runId) return; const { data } = await mhApi.getRun(runId); setRun(data); };

  return (
    <>
      <div className="page-toolbar"><span className="mh-count">{audits.length} site(s) · audit hebdo automatique (lundi)</span></div>
      <div className="table-container">
        <table>
          <thead><tr><th>Site</th><th>Type</th><th>Score global</th><th>Findings critiques</th><th>Dernier audit</th><th></th></tr></thead>
          <tbody>
            {audits.map(a => (
              <tr key={a.site_id} className={a.run_id ? 'mh-row' : ''} onClick={() => openRun(a.run_id)}>
                <td className="mh-site-cell">{a.name}</td>
                <td>{SITE_TYPES.find(t => t.value === a.site_type)?.label || a.site_type}</td>
                <td><ScoreChip score={a.global_score} /></td>
                <td>{a.critical_findings > 0 ? <span className="status-error">{a.critical_findings}</span> : '—'}</td>
                <td className="mh-muted">{a.run_at ? fmtDate(a.run_at) : 'jamais'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <button className="page-header-btn" disabled={running[a.site_id]} onClick={() => runNow(a.site_id)}>
                    {running[a.site_id] ? '⏳ En cours...' : '▶ Auditer'}
                  </button>
                </td>
              </tr>
            ))}
            {audits.length === 0 && <tr><td colSpan={6} className="mh-empty">Aucun site à auditer.</td></tr>}
          </tbody>
        </table>
      </div>
      {run && <RunDetail run={run} onClose={() => setRun(null)} reloadProjects={reloadProjects} reload={reload} isSuperAdmin={isSuperAdmin} />}
    </>
  );
}

function RunDetail({ run, onClose, reloadProjects, reload, isSuperAdmin }) {
  const [findings, setFindings] = useState(run.findings || []);
  const radarData = (run.scores || []).map(s => ({ dim: DIMENSION_LABELS[s.dimension] || s.dimension, score: Number(s.score) || 0 }));

  const convert = async (f) => {
    try { await mhApi.convertFinding(f.id); setFindings(fs => fs.map(x => x.id === f.id ? { ...x, status: 'converted' } : x)); reloadProjects && reloadProjects(); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };
  const dismiss = async (f) => {
    try { await mhApi.patchFinding(f.id, { status: 'dismissed' }); setFindings(fs => fs.map(x => x.id === f.id ? { ...x, status: 'dismissed' } : x)); }
    catch { /* */ }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Audit · {run.site_name} <ScoreChip score={run.global_score} /></h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="mh-run-grid">
            <div className="mh-radar">
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData} outerRadius={90}>
                  <PolarGrid /><PolarAngleAxis dataKey="dim" tick={{ fontSize: 11 }} /><PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                  <Radar dataKey="score" stroke="var(--color-accent)" fill="var(--color-accent)" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mh-scores">
              {(run.scores || []).map(s => (
                <div key={s.dimension} className="mh-score-row">
                  <ScoreChip score={s.score} /><strong>{DIMENSION_LABELS[s.dimension] || s.dimension}</strong>
                  <span className="mh-muted">{s.summary}</span>
                </div>
              ))}
            </div>
          </div>

          <h3>Plan d'actions ({findings.filter(f => f.status === 'open').length} ouvert(s))</h3>
          <div className="mh-findings">
            {findings.map(f => (
              <div key={f.id} className={`mh-finding ${f.status !== 'open' ? 'done' : ''}`}>
                <div className="mh-finding-head">
                  <span className={SEVERITY[f.severity]?.cls || 'status-info'}>{SEVERITY[f.severity]?.label || f.severity}</span>
                  <strong>{f.title}</strong>
                  <span className="mh-muted">{DIMENSION_LABELS[f.dimension] || f.dimension}</span>
                </div>
                {f.description && <p className="mh-finding-desc">{f.description}</p>}
                {f.recommendation && <p className="mh-finding-reco">💡 {f.recommendation}</p>}
                {f.status === 'open' ? (
                  <div className="mh-finding-actions">
                    <button className="page-header-btn" onClick={() => convert(f)}>→ Tâche</button>
                    <button className="page-header-btn" onClick={() => dismiss(f)}>Ignorer</button>
                  </div>
                ) : <span className="status-success mh-tag">{f.status === 'converted' ? 'Convertie en tâche' : 'Traité'}</span>}
              </div>
            ))}
            {findings.length === 0 && <p className="mh-muted">Aucun finding.</p>}
          </div>

          {run.report_md && <details className="mh-report"><summary>Rapport complet</summary><pre>{run.report_md}</pre></details>}
        </div>
      </div>
    </div>
  );
}
